import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import { paymentsService } from '../../services/paymentsService';
import SplitPanelContent from '../../components/SplitPanelContent';
import { trackExperimentClick } from '../../services/abExperimentTracking';
import TurnstileWidget from '../../components/forms/TurnstileWidget';
import {
  buildNameValueMap,
  computeEffectiveFieldState,
} from '../../../shared/formLogic.js';

// ── Helper: load a Google Font dynamically ────────────────────────────────────
const GOOGLE_FONT_MAP = {
  'Plus Jakarta Sans': 'Plus+Jakarta+Sans:wght@300;400;500;600;700',
  'DM Sans': 'DM+Sans:wght@300;400;500;600;700',
  'Manrope': 'Manrope:wght@300;400;500;600;700',
  'Inter': 'Inter:wght@300;400;500;600;700',
  'Roboto': 'Roboto:wght@300;400;500;700',
  'Lato': 'Lato:wght@300;400;700',
  'Poppins': 'Poppins:wght@300;400;500;600;700',
  'Nunito': 'Nunito:wght@300;400;500;600;700',
  'Open Sans': 'Open+Sans:wght@300;400;500;600;700',
  'JetBrains Mono': 'JetBrains+Mono:wght@300;400;500;600;700',
};

const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || !GOOGLE_FONT_MAP?.[fontFamily]) return;
  const id = `gfont-${fontFamily?.replace(/\s+/g, '-')}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${GOOGLE_FONT_MAP?.[fontFamily]}&display=swap`;
  document.head?.appendChild(link);
};

// Evaluate form.logicRules (ConditionalLogicPanel rules) against current form values.
// Returns field-level overrides keyed by fieldId and a set of page IDs to skip.
const evaluateLogicRules = (logicRules, formValues) => {
  const fieldOverrides = evaluateFieldLogicRules(logicRules, formValues);
  const pageLogicResult = evaluatePageLogicRules(logicRules, formValues);

  return { fieldOverrides, ...pageLogicResult };
};

// ── Custom field validation ───────────────────────────────────────────────────
// Imported from the shared utility so the viewer and the builder's PreviewModal
// always run identical validation logic.
import { validateField } from '../../utils/formValidation';
import {
  buildPageScopedFormValues,
  buildConvQuestionScreens,
  computeReachablePageIds,
  evaluateFieldLogicRules,
  evaluatePageLogicRules,
  getConversationalScreenPageIndex,
  resolveNextConversationalScreenIndex,
  resolveNextPageIndex,
} from '../../utils/convFieldSequence';
import { evaluateFormula, formatResult } from '../../utils/calcFieldUtils';

// ── Helper: darken a hex colour by a given amount (0–255) ────────────────────
const darkenHex = (hex, amount = 40) => {
  const clean = hex?.replace('#', '') || '';
  if (clean.length !== 6) return '#1e293b';
  const r = Math.max(0, parseInt(clean.slice(0, 2), 16) - amount);
  const g = Math.max(0, parseInt(clean.slice(2, 4), 16) - amount);
  const b = Math.max(0, parseInt(clean.slice(4, 6), 16) - amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Converts snake_case object keys to camelCase (top-level only for form row;
// nested JSONB blobs like multiStepConfig are stored with camelCase keys already)
const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

// ── Conversational mode: signature pad component ──────────────────────────────
const ConvSignaturePad = ({ value, onChange, borderColor, strokeColor }) => {
  const canvasRef = React.useRef(null);
  const drawingRef = React.useRef(false);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = strokeColor || '#111827';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [strokeColor]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw = (e) => {
    drawingRef.current = true;
    const ctx = canvasRef.current?.getContext('2d');
    const p = getPos(e);
    ctx?.beginPath();
    ctx?.moveTo(p.x, p.y);
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    const p = getPos(e);
    ctx?.lineTo(p.x, p.y);
    ctx?.stroke();
  };

  const endDraw = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange(canvasRef.current?.toDataURL() || '');
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height);
    onChange('');
  };

  return (
    <div className="mt-2 space-y-2">
      <canvas
        ref={canvasRef}
        width={480} height={160}
        className="w-full rounded-xl border-2 cursor-crosshair bg-white/60"
        style={{ borderColor: borderColor || '#d1d5db', touchAction: 'none' }}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
      />
      {value && (
        <button type="button" onClick={clear}
          className="text-xs opacity-50 hover:opacity-80 transition-opacity flex items-center gap-1">
          Clear signature
        </button>
      )}
    </div>
  );
};

const PublicFormViewer = () => {
  const { formId } = useParams();
  const [searchParams] = useSearchParams();
  // ?preview=true bypasses the published gate (used by Publish page preview button)
  const isPreviewMode = searchParams?.get('preview') === 'true';
  const fullFormId = formId || null;
  const experimentContext = {
    testId: searchParams?.get('ab_test'),
    variantId: searchParams?.get('ab_variant'),
    visitorId: searchParams?.get('ab_visitor'),
  };

  const [form, setForm] = useState(null);
  const [showPoweredBy, setShowPoweredBy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);
  const honeypotRef = useRef(null);

  // Managed Stripe Checkout state. Fulfilment is verified only through the
  // status endpoint because Checkout's browser return is not authoritative.
  const [checkoutState, setCheckoutState] = useState(null);
  const checkoutKeyStorage = `public-form-checkout-key-${fullFormId || 'unknown'}`;
  const checkoutRequestKeyRef = useRef((() => {
    try {
      return sessionStorage.getItem(checkoutKeyStorage) || crypto.randomUUID();
    } catch {
      return crypto.randomUUID();
    }
  })());
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');

  // Multi-step state
  const [currentStep, setCurrentStep] = useState(0);
  // Ref attached to the multi-step <form> so the footer bar can trigger native validation
  const footerFormRef = useRef(null);

  // Conversational mode state
  const [convFieldIndex, setConvFieldIndex] = useState(0);
  const [convDirection, setConvDirection] = useState('forward');
  const [convShake, setConvShake] = useState(false);
  const [convOutgoing, setConvOutgoing] = useState(null); // { screen, exitClass } for exit animation
  const [convReviewMode, setConvReviewMode] = useState(false); // show answer-review overlay
  const autoAdvanceTimerRef = useRef(null);
  const convOutgoingTimerRef = useRef(null);
  const convAdvanceRef = useRef(null); // stable ref so keyboard listener always calls current handler
  const convBackRef = useRef(null);    // stable ref for Back
  const convContextRef = useRef(null); // { currentField, formValues, handleChange } for arrow-key cycling
  const swipeTouchStartXRef = useRef(null); // tracks touch start X for swipe gesture in conversational mode
  const visitedPageIdsRef = useRef([]);
  const experimentClickTrackedRef = useRef(false);
  const turnstileEnabled = !isPreviewMode && form?.settings?.turnstileEnabled === true;
  const turnstileSiteKey = import.meta.env?.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    visitedPageIdsRef.current = [];
  }, [fullFormId]);

  const handleTurnstileToken = useCallback((token) => {
    setTurnstileToken(token || null);
  }, []);

  const handleTurnstileError = useCallback((message) => {
    if (message) setError(message);
  }, []);

  const renderTurnstile = () => {
    if (!turnstileEnabled) return null;
    return (
      <TurnstileWidget
        siteKey={turnstileSiteKey}
        onToken={handleTurnstileToken}
        onError={handleTurnstileError}
        resetSignal={turnstileResetSignal}
      />
    );
  };

  const renderHoneypot = () => (
    <div aria-hidden="true" className="absolute -left-[10000px] h-px w-px overflow-hidden">
      <label htmlFor="webforms-website">Leave this field blank</label>
      <input
        ref={honeypotRef}
        id="webforms-website"
        name="website"
        type="text"
        tabIndex="-1"
        autoComplete="off"
      />
    </div>
  );

  const recordExperimentClick = () => {
    if (
      experimentClickTrackedRef.current ||
      !experimentContext?.testId ||
      !experimentContext?.variantId ||
      !fullFormId
    ) return;

    experimentClickTrackedRef.current = true;
    trackExperimentClick({
      testId: experimentContext.testId,
      variantId: experimentContext.variantId,
      formId: fullFormId,
      visitorId: experimentContext.visitorId,
    });
  };

  // Load Google Font whenever the form's theme fontFamily changes
  useEffect(() => {
    if (form?.globalTheme?.fontFamily) {
      loadGoogleFont(form.globalTheme.fontFamily);
    }
  }, [form?.globalTheme?.fontFamily]);

  // Seed slider default values into formValues so they are present on submission
  // even when the respondent never drags the thumb.
  // NOTE: field-resolution order must match resolveAllFields() below.
  useEffect(() => {
    if (!form) return;
    const allFields = (() => {
      if (Array.isArray(form?.formElements) && form?.formElements?.length > 0) return form.formElements;
      if (Array.isArray(form?.pages) && form?.pages?.length > 0) {
        const all = form.pages.flatMap(p => p?.fields || p?.elements || []);
        if (all.length > 0) return all;
      }
      if (Array.isArray(form?.fields) && form?.fields?.length > 0) return form.fields;
      if (Array.isArray(form?.formFields) && form?.formFields?.length > 0) return form.formFields;
      return [];
    })();
    const fieldDefaults = {};
    for (const field of allFields) {
      const id = field?.id || field?.name;
      if (!id) continue;

      if (field?.type === 'slider') {
        const min = field?.sliderMin ?? 0;
        const max = field?.sliderMax ?? 100;
        fieldDefaults[id] = field?.sliderDefault ?? Math.round((min + max) / 2);
      } else if (field?.type === 'rating') {
        // Star-rating fields: default to the configured default or 1 (lowest visible star).
        // Property names follow the pattern: ratingMax (default 5), ratingDefault (default 1).
        const ratingMax = field?.ratingMax ?? 5;
        fieldDefaults[id] = field?.ratingDefault ?? Math.ceil(ratingMax / 2);
      } else if (field?.type === 'scale') {
        // Numeric-scale fields: seed the midpoint unless a default is configured.
        // Property names follow the pattern: scaleMin (default 1), scaleMax (default 10), scaleDefault.
        const scaleMin = field?.scaleMin ?? 1;
        const scaleMax = field?.scaleMax ?? 10;
        fieldDefaults[id] = field?.scaleDefault ?? Math.round((scaleMin + scaleMax) / 2);
      }
    }
    if (Object.keys(fieldDefaults).length === 0) return;
    setFormValues(prev => {
      // Only seed fields that have no value yet
      const patch = {};
      for (const [id, val] of Object.entries(fieldDefaults)) {
        if (prev[id] == null) patch[id] = val;
      }
      if (Object.keys(patch).length === 0) return prev;
      return { ...prev, ...patch };
    });
  }, [form]);

  // Inject conversational mode CSS keyframes once
  useEffect(() => {
    if (document.getElementById('conv-mode-styles')) return;
    const s = document.createElement('style');
    s.id = 'conv-mode-styles';
    s.textContent = `
      @keyframes convSlideInUp   { from { opacity:0; transform:translateY(52px);  } to { opacity:1; transform:translateY(0); } }
      @keyframes convSlideInDown { from { opacity:0; transform:translateY(-52px); } to { opacity:1; transform:translateY(0); } }
      @keyframes convExitUp      { from { opacity:1; transform:translateY(0);     } to { opacity:0; transform:translateY(-52px); } }
      @keyframes convExitDown    { from { opacity:1; transform:translateY(0);     } to { opacity:0; transform:translateY(52px);  } }
      @keyframes convShakeAnim   { 0%,100%{transform:translateX(0)} 15%,45%,75%{transform:translateX(-10px)} 30%,60%,90%{transform:translateX(10px)} }
      .conv-in-up    { animation: convSlideInUp   0.38s cubic-bezier(0.22,0.61,0.36,1) both; }
      .conv-in-down  { animation: convSlideInDown 0.38s cubic-bezier(0.22,0.61,0.36,1) both; }
      .conv-exit-up  { animation: convExitUp      0.35s cubic-bezier(0.22,0.61,0.36,1) both; pointer-events:none; }
      .conv-exit-down{ animation: convExitDown    0.35s cubic-bezier(0.22,0.61,0.36,1) both; pointer-events:none; }
      .conv-shake    { animation: convShakeAnim   0.5s ease both; }
    `;
    document.head.appendChild(s);
  }, []);

  // Keyboard handler for conversational mode
  useEffect(() => {
    const handler = (e) => {
      // Only active when a conversational advance handler is registered
      if (!convAdvanceRef.current) return;

      const tag = document.activeElement?.tagName?.toLowerCase();
      const activeEl = document.activeElement;

      if (e.key === 'Enter') {
        if (tag === 'textarea' || tag === 'select' || tag === 'button') return;
        e.preventDefault();
        convAdvanceRef.current();
        return;
      }

      // Backspace or ← on an empty text input → go back
      if ((e.key === 'Backspace' || e.key === 'ArrowLeft') && convBackRef.current) {
        if (tag === 'input') {
          const isEmpty = !activeEl?.value || activeEl?.value?.length === 0;
          if (!isEmpty) return; // let the key work normally in non-empty inputs
        } else if (tag === 'textarea' || tag === 'select') {
          return; // never intercept inside textarea/select
        }
        e.preventDefault();
        convBackRef.current();
        return;
      }

      // ArrowUp / ArrowDown cycle options in radio and select fields
      if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && convContextRef.current) {
        const { currentField: cf, currentFormValues, onHandleChange } = convContextRef.current;
        if (!cf || !['radio', 'select', 'dropdown'].includes(cf?.type)) return;
        const opts = (cf?.options || []).map(o => (typeof o === 'object' ? o?.value : o));
        if (!opts.length) return;
        e.preventDefault();
        const fid = cf?.id || cf?.name;
        const currIdx = opts.indexOf(currentFormValues?.[fid]);
        const nextIdx = e.key === 'ArrowDown'
          ? (currIdx + 1) % opts.length
          : (currIdx - 1 + opts.length) % opts.length;
        onHandleChange(fid, opts[nextIdx]);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      if (convOutgoingTimerRef.current) clearTimeout(convOutgoingTimerRef.current);
    };
  }, []);

  // Conversational keyboard shortcuts must stop while submission is handing off
  // to hosted Checkout. Otherwise Enter can re-trigger the final advance handler
  // while the Checkout session request is in flight.
  useEffect(() => {
    if (creatingIntent || checkoutState?.state === 'processing' || submitted) {
      convAdvanceRef.current = null;
      convBackRef.current = null;
      convContextRef.current = null;
    }
  }, [creatingIntent, checkoutState?.state, submitted]);

  useEffect(() => {
    const fetchForm = async () => {
      if (!fullFormId) {
        setNotFound(true);
        setShowPoweredBy(false);
        setLoading(false);
        return;
      }

      try {
        setShowPoweredBy(false);
        const { data, error } = await supabase
          ?.from('forms')
          ?.select('*')
          ?.eq('id', fullFormId)
          ?.maybeSingle();

        if (error) {
          console.error('Form fetch error:', error);
          setNotFound(true);
        } else if (!data) {
          setNotFound(true);
        } else if (!data?.published && !isPreviewMode) {
          // Only block unpublished forms when NOT in preview mode
          setNotFound(true);
        } else {
          setForm(toCamelCase(data));
          const { data: canShowPoweredBy, error: planError } = await supabase
            .rpc('should_show_public_form_powered_by', { p_form_id: fullFormId });
          setShowPoweredBy(!planError && canShowPoweredBy === true);
        }
      } catch (err) {
        console.error('Error fetching form:', err);
        setShowPoweredBy(false);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchForm();
  }, [fullFormId, isPreviewMode]);

  // ── Draft persistence: keep partially-filled answers across sign-in ────────
  // If the session expires mid-fill and the respondent navigates away to sign
  // in, in-memory state would be lost. Persist answers to sessionStorage keyed
  // by formId and restore them on mount so no work is lost.
  const draftKey = fullFormId ? `form-draft-${fullFormId}` : null;
  const draftRestoredRef = useRef(false);

  // Restore a saved draft on mount (before user input; slider/rating seeding
  // only fills empty values, so restored answers take precedence).
  useEffect(() => {
    if (!draftKey || draftRestoredRef.current) return;
    draftRestoredRef.current = true;
    try {
      const raw = sessionStorage.getItem(draftKey);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft && typeof draft === 'object' && !Array.isArray(draft)) {
        setFormValues(prev => ({ ...prev, ...draft }));
      }
    } catch (err) {
      console.error('Failed to restore form draft:', err);
    }
  }, [draftKey]);

  // Save answers on every change. File objects can't be serialised, so they
  // are stripped from the persisted draft (the respondent re-attaches files).
  useEffect(() => {
    if (!draftKey || !draftRestoredRef.current || submitted) return;
    try {
      const serialisable = Object.fromEntries(
        Object.entries(formValues).filter(([, v]) =>
          !(v instanceof File) &&
          !(Array.isArray(v) && v.some(x => x instanceof File))
        )
      );
      if (Object.keys(serialisable).length === 0) {
        sessionStorage.removeItem(draftKey);
      } else {
        sessionStorage.setItem(draftKey, JSON.stringify(serialisable));
      }
    } catch (err) {
      // Quota or privacy-mode failures shouldn't break the form
      console.error('Failed to save form draft:', err);
    }
  }, [draftKey, formValues, submitted]);

  const clearDraft = () => {
    if (!draftKey) return;
    try { sessionStorage.removeItem(draftKey); } catch { /* ignore */ }
  };

  useEffect(() => {
    const outcome = searchParams?.get('checkout');
    const sessionId = searchParams?.get('session_id');
    if (!outcome || isPreviewMode) return;
    if (outcome === 'cancel' || outcome === 'cancelled') {
      setCheckoutState({ state: 'canceled', message: 'Checkout was canceled. Your answers are still here; you can try again.' });
      setError('Checkout was canceled. Your answers are still saved; review them and try again when ready.');
      return;
    }
    if (!sessionId) return;
    let active = true;
    let attempts = 0;
    const reconcile = async () => {
      try {
        const result = await paymentsService.getCheckoutSessionStatus(sessionId, 'form_payment');
        if (!active) return;
        const status = String(result?.status || result?.state || '').toLowerCase();
        if (['fulfilled', 'complete', 'completed', 'succeeded', 'paid'].includes(status)) {
          try { sessionStorage.removeItem(checkoutKeyStorage); } catch { /* ignore */ }
          clearDraft();
          setSubmitted(true);
          setCheckoutState(null);
        } else if (['canceled', 'cancelled', 'expired', 'failed'].includes(status)) {
          checkoutRequestKeyRef.current = crypto.randomUUID();
          try { sessionStorage.setItem(checkoutKeyStorage, checkoutRequestKeyRef.current); } catch { /* ignore */ }
          setCheckoutState({ state: status === 'cancelled' ? 'canceled' : status, message: result?.message });
          setError(result?.message || `Checkout ${status}. Your answers are still saved; please try again.`);
        } else {
          setCheckoutState({ state: 'processing', message: 'Your payment is being confirmed. This usually takes a moment.' });
          if (++attempts < 40) setTimeout(reconcile, Math.min(5000, 1500 + attempts * 250));
          else setCheckoutState({ state: 'failed', message: 'Payment confirmation is taking longer than expected. Refresh this page to check again; your answers are still saved.' });
        }
      } catch {
        if (!active) return;
        setCheckoutState({ state: 'processing', message: 'We are still confirming your payment. Retrying automatically…' });
        if (++attempts < 40) setTimeout(reconcile, Math.min(5000, 1500 + attempts * 250));
        else setCheckoutState({ state: 'failed', message: 'We could not refresh payment status. Refresh this page to try again; your answers are still saved.' });
      }
    };
    reconcile();
    return () => { active = false; };
  }, [searchParams, isPreviewMode]);

  const handleChange = (fieldId, value) => {
    recordExperimentClick();
    const newValues = { ...formValues, [fieldId]: value };
    setFormValues(newValues);
    // Clear the validation error for this field as soon as the user edits it,
    // AND clear errors for any fields that became hidden as a result of this change.
    setValidationErrors(prev => {
      if (!Object.keys(prev).length) return prev;
      const next = { ...prev };
      // Always clear the changed field's own error
      delete next[fieldId];
      // Clear errors for fields now hidden by the updated values
      const allFields = (() => {
        if (Array.isArray(form?.formElements) && form?.formElements?.length > 0) return form.formElements;
        if (Array.isArray(form?.pages) && form?.pages?.length > 0) {
          const f = form.pages.flatMap(p => p?.fields || p?.elements || []);
          if (f.length > 0) return f;
        }
        if (Array.isArray(form?.fields) && form?.fields?.length > 0) return form.fields;
        if (Array.isArray(form?.formFields) && form?.formFields?.length > 0) return form.formFields;
        return [];
      })();
      if (allFields.length > 0) {
        const nvm = buildNameValueMap(allFields, newValues);
        const logicOverrides = evaluateLogicRules(form?.logicRules, newValues).fieldOverrides;
        allFields.forEach(f => {
          const fid = f?.id || f?.name;
          if (fid && next[fid]) {
            const state = computeEffectiveFieldState(f, nvm, logicOverrides);
            if (!state.visible) delete next[fid];
          }
        });
      }
      return next;
    });
  };

  const handleBlur = (fieldId, field) => {
    const err = validateField(field, formValues?.[fieldId]);
    setValidationErrors(prev => {
      if (!err && !prev[fieldId]) return prev;
      if (!err) { const next = { ...prev }; delete next[fieldId]; return next; }
      return { ...prev, [fieldId]: err };
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    // In preview mode, just show success without submitting
    if (isPreviewMode) {
      setSubmitted(true);
      return;
    }

    // ── Evaluate dependency rules before committing to submit ─────────────────
    // Compute field states inline so we always use the latest formValues.
    const currentAllFields = (() => {
      if (Array.isArray(form?.formElements) && form?.formElements?.length > 0) return form.formElements;
      if (Array.isArray(form?.pages) && form?.pages?.length > 0) {
        const all = form.pages.flatMap(p => p?.fields || p?.elements || []);
        if (all.length > 0) return all;
      }
      return [];
    })();
    const currentNVM = buildNameValueMap(currentAllFields, formValues);
    const currentLogicResult = evaluateLogicRules(form?.logicRules, formValues);
    const submissionSteps = form?.multiStepConfig?.steps || [];
    const submissionLayoutMode =
      form?.settings?.layoutMode ||
      (form?.multiStepConfig?.conversationalMode === true ? 'conversational'
        : form?.multiStepConfig?.enabled ? 'multi-step'
        : 'standard');
    const routeAwareValidation =
      ['multi-step', 'conversational'].includes(submissionLayoutMode) &&
      submissionSteps.length > 1;
    const reachablePageIds = routeAwareValidation
      ? computeReachablePageIds(submissionSteps, form?.logicRules, formValues)
      : null;
    const fieldPageIds = routeAwareValidation
      ? new Map(submissionSteps.flatMap(step =>
          (step?.fieldIds || []).map(fieldId => [fieldId, step?.id])
        ))
      : null;

    // Block submission if any visible field has an active setError dependency
    const firstDepError = currentAllFields
      .map(f => computeEffectiveFieldState(f, currentNVM, currentLogicResult.fieldOverrides))
      .find(s => s.visible && s.depError);
    if (firstDepError) {
      setError(firstDepError.depError);
      return;
    }

    // ── Run custom validation rules on all visible fields ─────────────────────
    const newValidationErrors = {};
    let hasValidationError = false;
    for (const f of currentAllFields) {
      const id = f?.id || f?.name;
      const fieldPageId = fieldPageIds?.get(id);
      if (fieldPageId && !reachablePageIds?.has(fieldPageId)) continue;
      const state = computeEffectiveFieldState(f, currentNVM, currentLogicResult.fieldOverrides);
      if (!state.visible) continue;
      // Merge the effective required flag into the field so validateField uses it
      const err = validateField({ ...f, required: state.required }, formValues?.[id]);
      if (err) { newValidationErrors[id] = err; hasValidationError = true; }
    }
    if (hasValidationError) {
      setValidationErrors(newValidationErrors);
      setError('Please fix the highlighted errors before submitting.');
      return;
    }
    setValidationErrors({});

    if (turnstileEnabled && !turnstileToken) {
      setError('Please complete the spam-protection check before submitting.');
      return;
    }

    // ── Filter out values for fields that are currently hidden ─────────────
    const visibleIds = new Set(
      currentAllFields
        .filter(f => computeEffectiveFieldState(f, currentNVM, currentLogicResult.fieldOverrides).visible)
        .map(f => f?.id || f?.name)
        .filter(Boolean)
    );
    // Separate real File objects from JSON-serialisable values. Files are sent
    // as multipart parts so the Edge Function can store them in Supabase Storage;
    // the JSON payload carries name/size/type metadata for validation.
    const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB, mirrors server + bucket limit
    const pendingFiles = {}; // fieldId → File[]
    const serializeValue = (id, v) => {
      if (v instanceof File) {
        pendingFiles[id] = [v];
        return { _fileUpload: true, name: v.name, size: v.size, type: v.type };
      }
      if (Array.isArray(v) && v.length > 0 && v.every(x => x instanceof File)) {
        pendingFiles[id] = v;
        return v.map(f => ({ _fileUpload: true, name: f.name, size: f.size, type: f.type }));
      }
      return v;
    };
    const filteredValues = Object.fromEntries(
      Object.entries(formValues)
        .filter(([id]) => visibleIds.has(id))
        .map(([id, v]) => [id, serializeValue(id, v)])
    );

    // Client-side size check for a friendly error before any network round-trip
    // (the server and the storage bucket enforce the same limit authoritatively).
    for (const files of Object.values(pendingFiles)) {
      const tooBig = files.find(f => f.size > MAX_FILE_BYTES);
      if (tooBig) {
        setError(`File "${tooBig.name}" is larger than the 10 MB limit. Please choose a smaller file.`);
        return;
      }
    }
    const hasFiles = Object.keys(pendingFiles).length > 0;

    // ── Payment-enabled forms: persist submission and redirect to Checkout ──
    if (form?.paymentEnabled && form?.paymentSettings) {
      const settings = form.paymentSettings;
      const amount = settings?.mode === 'variable'
        ? parseFloat(paymentAmount)
        : parseFloat(settings?.amount);

      if (!amount || isNaN(amount) || amount <= 0) {
        setError(
          settings?.mode === 'variable'
            ? 'Please enter a valid payment amount.'
            : 'Invalid payment amount configured for this form.'
        );
        return;
      }

      setCreatingIntent(true);
      setError(null);

      try {
        try { sessionStorage.setItem(checkoutKeyStorage, checkoutRequestKeyRef.current); } catch { /* ignore */ }
        const result = await paymentsService.createCheckoutSession({
          formId: fullFormId,
          submissionData: filteredValues,
          amount: settings?.mode === 'variable' ? amount : undefined,
          protection: {
            turnstileToken,
            honeypot: honeypotRef.current?.value || '',
          },
          experimentContext,
          files: pendingFiles,
          idempotencyKey: checkoutRequestKeyRef.current,
        });
        if (turnstileEnabled) {
          setTurnstileToken(null);
          setTurnstileResetSignal(value => value + 1);
        }
        if (result?.blocked) {
          clearDraft();
          setSubmitted(true);
          return;
        }
        // Keep the draft until the webhook-backed status says fulfilled. This
        // also makes a Checkout cancellation return respondents to their work.
        window.location.assign(result.url);
      } catch (err) {
        console.error('Error creating payment intent:', err);
        if (turnstileEnabled) {
          setTurnstileToken(null);
          setTurnstileResetSignal(value => value + 1);
        }
        if (err?.fieldErrors && typeof err.fieldErrors === 'object') {
          setValidationErrors(err.fieldErrors);
          setError('Please fix the highlighted errors before submitting.');
          return;
        }
        // Use the server-provided message when available so Stripe
        // mis-configuration is immediately actionable.
        const serverMsg = err?.message || err?.details || err?.error;
        setError(
          serverMsg && serverMsg.length < 300
            ? serverMsg
            : 'Failed to initialize payment. Please try again.'
        );
      } finally {
        setCreatingIntent(false);
      }
      return;
    }

    // ── Non-payment form: submit directly ─────────────────────────────────
    setSubmitting(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
      const anonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;
      const edgeFnUrl = `${supabaseUrl}/functions/v1/handle-form-submission`;

      const payload = {
        formId: fullFormId,
        submissionData: filteredValues,
        userAgent: navigator?.userAgent || null,
        source: 'web',
        turnstileToken,
        honeypot: honeypotRef.current?.value || '',
        ...(experimentContext?.testId && experimentContext?.variantId ? {
          abTestId: experimentContext.testId,
          abVariantId: experimentContext.variantId,
          abVisitorId: experimentContext.visitorId,
        } : {}),
      };

      let response;
      if (hasFiles) {
        // Multipart request: JSON payload + one part per file so the server
        // can store the real files in Supabase Storage.
        const fd = new FormData();
        fd.append('payload', JSON.stringify(payload));
        for (const [fieldId, files] of Object.entries(pendingFiles)) {
          for (const f of files) fd.append(`file:${fieldId}`, f, f.name);
        }
        response = await fetch(edgeFnUrl, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${anonKey}` },
          body: fd,
        });
      } else {
        response = await fetch(edgeFnUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${anonKey}`,
          },
          body: JSON.stringify(payload),
        });
      }

      const result = await response?.json();

      if (response?.status === 401) {
        setError('Please sign in again to submit. Your answers have been saved — you can continue after signing in.');
      } else if (!response?.ok || !result?.success) {
        // If the server returned per-field errors (422 custom-rule failure),
        // show them inline just like client-side validation errors.
        if (result?.fieldErrors && typeof result.fieldErrors === 'object') {
          setValidationErrors(result.fieldErrors);
          setError('Please fix the highlighted errors before submitting.');
        } else {
          const errMsg = result?.error || 'There was a problem submitting the form. Please try again.';
          setError(errMsg);
        }
        if (turnstileEnabled) {
          setTurnstileToken(null);
          setTurnstileResetSignal(value => value + 1);
        }
      } else {
        if (turnstileEnabled) {
          setTurnstileToken(null);
          setTurnstileResetSignal(value => value + 1);
        }
        clearDraft();
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('There was a problem submitting the form. Please try again.');
      if (turnstileEnabled) {
        setTurnstileToken(null);
        setTurnstileResetSignal(value => value + 1);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field, nameValueMap = {}, logicOverrides = {}) => {
    // ── Conditional logic — compute effective field state ───────────────────
    const fieldState = computeEffectiveFieldState(field, nameValueMap, logicOverrides);
    if (!fieldState.visible) return null;

    const fieldId = field?.id || field?.name;
    const label = field?.label || field?.name || 'Field';
    const required = fieldState.required;
    const disabled = fieldState.disabled;
    const depError = fieldState.depError;
    const placeholder = field?.placeholder || '';
    // Normalise type: convert snake_case stored values back to the expected form
    // e.g. 'full_name' → 'fullName' won't match, but 'input' is already correct.
    // The form builder stores type as 'input', 'email', 'phone', 'media', etc.
    const type = field?.type || 'text';

    // ── Per-field theme styles derived from globalTheme ──────────────────────
    const gt = form?.globalTheme || {};
    const fs = field?.fieldStyle || {};
    const accentColor = gt?.accentColor || '#0e7572';
    const borderRadius = fs?.borderRadius != null
      ? `${fs?.borderRadius}px`
      : gt?.borderRadius != null
      ? `${gt?.borderRadius}px`
      : '6px';

    const inputInlineStyle = {
      backgroundColor: fs?.inputBg || gt?.inputBackground || '#ffffff',
      color: fs?.textColor || gt?.inputTextColor || '#111827',
      borderRadius,
      borderColor: fs?.borderColor || accentColor,
      fontFamily: gt?.fontFamily || undefined,
      fontSize: fs?.fontSize != null
        ? `${fs?.fontSize}px`
        : gt?.fontSize
        ? `${gt?.fontSize}px`
        : undefined,
      fontWeight: fs?.inputFontWeight || gt?.fontWeight || undefined,
    };

    const labelInlineStyle = {
      color: fs?.labelColor || gt?.labelColor || '#111827',
      fontFamily: gt?.fontFamily || undefined,
      fontSize: fs?.fontSize != null
        ? `${fs?.fontSize}px`
        : gt?.fontSize
        ? `${gt?.fontSize}px`
        : undefined,
      fontWeight: fs?.fontWeight || fs?.labelFontWeight || gt?.fontWeight || undefined,
      textAlign: fs?.contentTextAlign || fs?.labelTextAlign || undefined,
    };

    const inputClass =
      'w-full px-3 py-2.5 border text-sm focus:outline-none focus:ring-2 transition-colors';

    const isSideLayout = fs?.labelAlign === 'side';

    const labelEl = isSideLayout ? (
      <label
        className="shrink-0 text-sm font-medium pt-1.5"
        style={{ ...labelInlineStyle, minWidth: '100px', maxWidth: '40%' }}
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    ) : (
      <label className="block text-sm font-medium mb-1.5" style={labelInlineStyle}>
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    );

    // Description: helper text set in the builder, shown beneath the label
    const descriptionEl = field?.description ? (
      <p className="text-xs text-gray-500 leading-snug -mt-1 mb-1">{field.description}</p>
    ) : null;

    // fieldHeaderEl: label + optional description — used in stacked layout
    const fieldHeaderEl = (
      <>
        {labelEl}
        {descriptionEl}
      </>
    );

    // Wraps label + input content in stacked or side-by-side layout.
    const wrapField = (inputAndErrors) => {
      if (isSideLayout) {
        return (
          <div key={fieldId} className="flex flex-row items-start gap-3">
            {labelEl}
            <div className="flex-1 min-w-0">
              {descriptionEl}
              {inputAndErrors}
            </div>
          </div>
        );
      }
      return (
        <div key={fieldId} className="space-y-1">
          {fieldHeaderEl}
          {inputAndErrors}
        </div>
      );
    };

    switch (type) {
      case 'textarea':
        return wrapField(
          <>
            <textarea
              className={`${inputClass} resize-none`}
              style={inputInlineStyle}
              rows={4}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              value={formValues?.[fieldId] || ''}
              onBlur={() => handleBlur(fieldId, field)}
              onChange={(e) => handleChange(fieldId, e?.target?.value)}
            />
            {depError && <p className="text-xs text-red-500 mt-1">{depError}</p>}
            {!depError && validationErrors?.[fieldId] && <p className="text-xs text-red-500 mt-1">{validationErrors[fieldId]}</p>}
          </>
        );

      case 'select': case 'dropdown':
        return wrapField(
          <>
            <select
              className={inputClass}
              style={inputInlineStyle}
              required={required}
              disabled={disabled}
              value={formValues?.[fieldId] || ''}
              onBlur={() => handleBlur(fieldId, field)}
              onChange={(e) => handleChange(fieldId, e?.target?.value)}
            >
              <option value="">{placeholder || 'Select an option'}</option>
              {(field?.options || [])?.map((opt, i) => (
                <option key={i} value={typeof opt === 'object' ? opt?.value : opt}>
                  {typeof opt === 'object' ? opt?.label : opt}
                </option>
              ))}
            </select>
            {depError && <p className="text-xs text-red-500 mt-1">{depError}</p>}
            {!depError && validationErrors?.[fieldId] && <p className="text-xs text-red-500 mt-1">{validationErrors[fieldId]}</p>}
          </>
        );

      case 'radio':
        return wrapField(
          <>
            <div className="space-y-2">
              {(field?.options || [])?.map((opt, i) => {
                const val = typeof opt === 'object' ? opt?.value : opt;
                const lbl = typeof opt === 'object' ? opt?.label : opt;
                return (
                  <label key={i} className={`flex items-center gap-2 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="radio"
                      name={fieldId}
                      value={val}
                      checked={formValues?.[fieldId] === val}
                      onChange={() => !disabled && handleChange(fieldId, val)}
                      required={required}
                      disabled={disabled}
                      style={{ accentColor }}
                    />
                    <span className="text-sm" style={{ color: labelInlineStyle?.color }}>{lbl}</span>
                  </label>
                );
              })}
            </div>
            {depError && <p className="text-xs text-red-500 mt-1">{depError}</p>}
            {!depError && validationErrors?.[fieldId] && <p className="text-xs text-red-500 mt-1">{validationErrors[fieldId]}</p>}
          </>
        );

      case 'checkbox':
        return wrapField(
          <>
            <div className="space-y-2">
              {(field?.options || [])?.map((opt, i) => {
                const val = typeof opt === 'object' ? opt?.value : opt;
                const lbl = typeof opt === 'object' ? opt?.label : opt;
                const checked = Array.isArray(formValues?.[fieldId])
                  ? formValues?.[fieldId]?.includes(val)
                  : false;
                return (
                  <label key={i} className={`flex items-center gap-2 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      value={val}
                      checked={checked}
                      disabled={disabled}
                      onChange={(e) => {
                        if (disabled) return;
                        const prev = Array.isArray(formValues?.[fieldId]) ? formValues?.[fieldId] : [];
                        const newVal = e?.target?.checked
                          ? [...prev, val]
                          : prev?.filter((v) => v !== val);
                        handleChange(fieldId, newVal);
                        // For required checkbox groups, show or clear the required error
                        // immediately after each interaction so the user gets instant feedback.
                        if (required) {
                          setValidationErrors(p => {
                            if (newVal.length === 0) {
                              return { ...p, [fieldId]: field?.requiredMessage || 'This field is required' };
                            }
                            const next = { ...p };
                            delete next[fieldId];
                            return next;
                          });
                        }
                      }}
                      style={{ accentColor }}
                    />
                    <span className="text-sm" style={{ color: labelInlineStyle?.color }}>{lbl}</span>
                  </label>
                );
              })}
            </div>
            {depError && <p className="text-xs text-red-500 mt-1">{depError}</p>}
            {!depError && validationErrors?.[fieldId] && <p className="text-xs text-red-500 mt-1">{validationErrors[fieldId]}</p>}
          </>
        );

      case 'number':
      case 'date':
      case 'email':
      case 'phone': {
        const tIconName = fs?.iconName;
        const tIconPos  = fs?.iconPosition || 'left';
        const ICON_PAD  = 32;
        const tStyle = tIconName ? {
          ...inputInlineStyle,
          paddingLeft:  tIconPos === 'left'  ? `${ICON_PAD}px` : inputInlineStyle?.paddingLeft,
          paddingRight: tIconPos === 'right' ? `${ICON_PAD}px` : inputInlineStyle?.paddingRight,
        } : inputInlineStyle;
        const tInputEl = (
          <input
            type={type === 'email' ? 'email' : type === 'phone' ? 'tel' : type === 'date' ? 'date' : 'number'}
            className={inputClass}
            style={tStyle}
            placeholder={
              type === 'email' ? (placeholder || 'you@example.com')
              : type === 'phone' ? (placeholder || '+1 (555) 000-0000')
              : placeholder
            }
            required={required}
            disabled={disabled}
            value={formValues?.[fieldId] || ''}
            onBlur={() => handleBlur(fieldId, field)}
            onChange={(e) => handleChange(fieldId, e?.target?.value)}
          />
        );
        return wrapField(
          <>
            {tIconName ? (
              <div className="relative">
                {tInputEl}
                <div
                  className="absolute inset-y-0 flex items-center pointer-events-none"
                  style={{ [tIconPos === 'right' ? 'right' : 'left']: '9px' }}
                >
                  <Icon name={tIconName} size={15} color={fs?.iconColor || inputInlineStyle?.color || '#9ca3af'} />
                </div>
              </div>
            ) : tInputEl}
            {depError && <p className="text-xs text-red-500 mt-1">{depError}</p>}
            {!depError && validationErrors?.[fieldId] && <p className="text-xs text-red-500 mt-1">{validationErrors[fieldId]}</p>}
          </>
        );
      }

      case 'heading':
        return (
          <div key={fieldId}>
            <h2 className="text-xl font-semibold" style={{ color: labelInlineStyle?.color, fontFamily: labelInlineStyle?.fontFamily }}>{label}</h2>
          </div>
        );

      case 'paragraph':
        return (
          <div key={fieldId}>
            <p className="text-sm leading-relaxed" style={{ color: labelInlineStyle?.color, fontFamily: labelInlineStyle?.fontFamily }}>{field?.content || label}</p>
          </div>
        );

      case 'divider':
        return <hr key={fieldId} className="border-gray-200" style={{ borderColor: accentColor + '40' }} />;

      case 'media':
        // Resolve image size: check fieldStyle.imageSize first, then top-level imageSize
        const imgSize = field?.fieldStyle?.imageSize ?? field?.imageSize ?? 100;
        // Apply the same default fallback as FormCanvas does in the form builder
        const DEFAULT_IMG = '/assets/images/web-forms-logo-1769800970615.png';
        const rawSrc = field?.src || DEFAULT_IMG;
        // Resolve relative paths (starting with /) to absolute URLs so they load
        // correctly when the form is embedded on any domain or opened in preview
        const resolvedSrc = rawSrc?.startsWith('/')
          ? `${window?.location?.origin}${rawSrc}`
          : rawSrc;
        return (
          <div key={fieldId} className="flex justify-center">
            <img
              src={resolvedSrc}
              alt={field?.alt || label || 'Form image'}
              style={{ width: `${imgSize}%`, maxWidth: '100%' }}
              className="rounded-lg object-contain"
              onError={(e) => {
                // If the resolved URL fails, try the absolute default as a last resort
                const fallbackAbsolute = `${window?.location?.origin}${DEFAULT_IMG}`;
                if (e?.currentTarget?.src !== fallbackAbsolute) {
                  e.currentTarget.src = fallbackAbsolute;
                } else {
                  e.currentTarget.style.display = 'none';
                }
              }}
            />
          </div>
        );

      case 'toggle': {
        const isChecked = formValues?.[fieldId] === 'true' || formValues?.[fieldId] === true;
        const onLabel  = field?.onLabel  || 'On';
        const offLabel = field?.offLabel || 'Off';
        const toggleColor = accentColor;
        return wrapField(
          <>
            <div className="flex items-center gap-3">
              <button
                type="button"
                role="switch"
                aria-checked={isChecked}
                disabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  const next = !isChecked;
                  handleChange(fieldId, String(next));
                  if (required) {
                    setValidationErrors(p => {
                      if (!next) return { ...p, [fieldId]: field?.requiredMessage || 'This field is required' };
                      const n = { ...p }; delete n[fieldId]; return n;
                    });
                  }
                }}
                className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                style={{
                  backgroundColor: isChecked ? toggleColor : '#d1d5db',
                  '--tw-ring-color': toggleColor,
                }}
              >
                <span
                  className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm transition-all"
                  style={{ left: isChecked ? 'calc(100% - 20px)' : '4px' }}
                />
              </button>
              <span className="text-sm" style={{ color: labelInlineStyle?.color }}>
                {isChecked ? onLabel : offLabel}
              </span>
            </div>
            {depError && <p className="text-xs text-red-500 mt-1">{depError}</p>}
            {!depError && validationErrors?.[fieldId] && <p className="text-xs text-red-500 mt-1">{validationErrors[fieldId]}</p>}
          </>
        );
      }

      case 'rating': {
        const ratingMax     = Math.max(1, field?.ratingMax ?? 5);
        const ratingDefault = Math.min(ratingMax, Math.max(1, field?.ratingDefault ?? Math.ceil(ratingMax / 2)));
        const currentRating = formValues?.[fieldId] != null ? Math.min(ratingMax, Math.max(1, Number(formValues?.[fieldId]))) : ratingDefault;
        return (
          <div key={fieldId} className="space-y-1">
            {fieldHeaderEl}
            <div className="flex items-center gap-0.5">
              {Array.from({ length: ratingMax }, (_, i) => {
                const starVal = i + 1;
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={disabled}
                    onClick={() => !disabled && handleChange(fieldId, starVal)}
                    style={{
                      fontSize: '28px',
                      color: starVal <= currentRating ? accentColor : '#d1d5db',
                      background: 'none',
                      border: 'none',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      padding: '0 2px',
                      lineHeight: 1,
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >★</button>
                );
              })}
            </div>
            {depError && <p className="text-xs text-red-500 mt-1">{depError}</p>}
            {!depError && validationErrors?.[fieldId] && <p className="text-xs text-red-500 mt-1">{validationErrors[fieldId]}</p>}
          </div>
        );
      }

      case 'scale': {
        const scaleMin     = field?.scaleMin     ?? 1;
        const scaleMax     = Math.max(scaleMin + 1, field?.scaleMax ?? 10);
        const scaleDefault = Math.min(scaleMax, Math.max(scaleMin, field?.scaleDefault ?? Math.round((scaleMin + scaleMax) / 2)));
        const currentScale = formValues?.[fieldId] != null ? Math.min(scaleMax, Math.max(scaleMin, Number(formValues?.[fieldId]))) : scaleDefault;
        return (
          <div key={fieldId} className="space-y-1">
            {fieldHeaderEl}
            <div className="flex flex-wrap gap-1.5">
              {Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => {
                const val = scaleMin + i;
                const isSelected = val === currentScale;
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={disabled}
                    onClick={() => !disabled && handleChange(fieldId, val)}
                    className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: isSelected ? accentColor : (fs?.inputBg || gt?.inputBackground || '#ffffff'),
                      borderColor: isSelected ? accentColor : (fs?.borderColor || accentColor),
                      color: isSelected ? '#ffffff' : (fs?.textColor || gt?.inputTextColor || '#111827'),
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    {val}
                  </button>
                );
              })}
            </div>
            {depError && <p className="text-xs text-red-500 mt-1">{depError}</p>}
            {!depError && validationErrors?.[fieldId] && <p className="text-xs text-red-500 mt-1">{validationErrors[fieldId]}</p>}
          </div>
        );
      }

      case 'slider': {
        const sliderMin     = field?.sliderMin     ?? 0;
        const sliderMax     = field?.sliderMax     ?? 100;
        const sliderStep    = field?.sliderStep    ?? 1;
        const sliderDefault = field?.sliderDefault ?? Math.round((sliderMin + sliderMax) / 2);
        const sliderVal     = formValues?.[fieldId] != null ? Number(formValues?.[fieldId]) : sliderDefault;
        return (
          <div key={fieldId} className="space-y-1">
            {fieldHeaderEl}
            <div className="space-y-1">
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={sliderMin}
                  max={sliderMax}
                  step={sliderStep}
                  value={sliderVal}
                  disabled={disabled}
                  className={`flex-1 h-2 rounded-lg appearance-none ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  style={{ accentColor }}
                  onChange={(e) => handleChange(fieldId, e?.target?.value)}
                  onBlur={() => handleBlur(fieldId, field)}
                />
                <span className="text-sm font-medium w-9 text-right flex-shrink-0" style={{ color: inputInlineStyle?.color }}>
                  {sliderVal}
                </span>
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>{sliderMin}</span>
                <span>{sliderMax}</span>
              </div>
            </div>
            {depError && <p className="text-xs text-red-500 mt-1">{depError}</p>}
            {!depError && validationErrors?.[fieldId] && <p className="text-xs text-red-500 mt-1">{validationErrors[fieldId]}</p>}
          </div>
        );
      }

      case 'file': {
        const selectedFile = formValues?.[fieldId];
        return (
          <div key={fieldId} className="space-y-1">
            {fieldHeaderEl}
            <label className="block cursor-pointer">
              <div
                className="rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors hover:border-opacity-80"
                style={{
                  borderColor: fs?.borderColor || '#d1d5db',
                  borderRadius: fs?.borderRadius != null ? `${fs.borderRadius}px` : undefined,
                  backgroundColor: fs?.inputBg || 'transparent',
                }}
              >
                <Icon name="Upload" size={22} color="#9ca3af" className="mx-auto mb-1" />
                <p className="text-sm font-medium text-gray-700">
                  {selectedFile?.name ? selectedFile.name : 'Click to choose a file'}
                </p>
                {selectedFile && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {(selectedFile.size / 1024).toFixed(1)} KB · {selectedFile.type || 'unknown type'}
                  </p>
                )}
                {!selectedFile && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {field?.accept ? `Accepted: ${field.accept}` : 'Any file type'}
                  </p>
                )}
              </div>
              <input
                type="file"
                className="sr-only"
                disabled={disabled}
                accept={field?.accept || undefined}
                multiple={field?.multiple || false}
                onChange={(e) => {
                  const file = field?.multiple ? Array.from(e.target.files || []) : (e.target.files?.[0] || null);
                  handleChange(fieldId, file);
                }}
              />
            </label>
            {depError && <p className="text-xs text-red-500 mt-1">{depError}</p>}
            {!depError && validationErrors?.[fieldId] && <p className="text-xs text-red-500 mt-1">{validationErrors[fieldId]}</p>}
          </div>
        );
      }

      case 'input': //'input' is the type used by the form builder for generic text fields
      // falls through intentionally to default
      // eslint-disable-next-line no-fallthrough

      default: {
        const fieldIconName = fs?.iconName;
        const fieldIconPos  = fs?.iconPosition || 'left';
        const ICON_SPACE    = 32; // px to reserve so text doesn't overlap the icon
        const iconAdjustedStyle = fieldIconName ? {
          ...inputInlineStyle,
          paddingLeft:  fieldIconPos === 'left'  ? `${ICON_SPACE}px` : inputInlineStyle?.paddingLeft,
          paddingRight: fieldIconPos === 'right' ? `${ICON_SPACE}px` : inputInlineStyle?.paddingRight,
        } : inputInlineStyle;

        const inputEl = (
          <input
            type="text"
            className={inputClass}
            style={iconAdjustedStyle}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            value={formValues?.[fieldId] || ''}
            onBlur={() => handleBlur(fieldId, field)}
            onChange={(e) => handleChange(fieldId, e?.target?.value)}
          />
        );

        return wrapField(
          <>
            {fieldIconName ? (
              <div className="relative">
                {inputEl}
                <div
                  className="absolute inset-y-0 flex items-center pointer-events-none"
                  style={{ [fieldIconPos === 'right' ? 'right' : 'left']: '9px' }}
                >
                  <Icon name={fieldIconName} size={15} color={fs?.iconColor || inputInlineStyle?.color || '#9ca3af'} />
                </div>
              </div>
            ) : inputEl}
            {depError && <p className="text-xs text-red-500 mt-1">{depError}</p>}
            {!depError && validationErrors?.[fieldId] && <p className="text-xs text-red-500 mt-1">{validationErrors[fieldId]}</p>}
          </>
        );
      }
    }
  };

  // ── Render calculated field results (read-only, live-updating) ───────────────
  // Renders all enabled calc fields that have showInForm !== false as a styled
  // read-only display.  nameValueMap already contains both field.name→value and
  // field.id→value so formula token resolution works correctly.
  const renderCalcFields = (currentNameValueMap) => {
    const calcFields = form?.calcFields || [];
    const visible = calcFields?.filter(cf => cf?.enabled && cf?.showInForm !== false);
    if (visible?.length === 0) return null;

    const gt = form?.globalTheme || {};
    const accentColor = gt?.accentColor || '#0e7572';
    const labelColor = gt?.labelColor || '#111827';
    const fontFamily = gt?.fontFamily || undefined;

    return (
      <div className="space-y-3">
        {visible?.map(cf => {
          const raw = evaluateFormula(cf?.formula, currentNameValueMap, cf?.products || []);
          const isError = raw === 'Error' || raw === 'Invalid formula';
          const isEmpty = raw === '';
          const formatted = isEmpty
            ? '—'
            : isError
            ? raw
            : formatResult(raw, cf?.format, cf?.currency, cf?.decimals, cf?.prefix, cf?.suffix);

          return (
            <div
              key={cf?.id}
              className="flex items-center justify-between px-4 py-3 rounded-xl border-2"
              style={{
                borderColor: `${accentColor}40`,
                backgroundColor: `${accentColor}08`,
                fontFamily,
              }}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Icon name="Calculator" size={15} color={accentColor} className="flex-shrink-0" />
                <span
                  className="text-sm font-medium truncate"
                  style={{ color: labelColor }}
                >
                  {cf?.label || 'Total'}
                </span>
              </div>
              <span
                className={`text-base font-bold flex-shrink-0 ml-4 ${isError ? 'text-red-500' : ''}`}
                style={isError ? undefined : { color: accentColor }}
              >
                {formatted}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Loading state ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-[#0e7572] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading form…</p>
        </div>
      </div>
    );
  }

  // ── Not found state ──────────────────────────────────────────────────────────
  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="FileX" size={28} color="#9ca3af" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Form Not Found</h1>
          <p className="text-sm text-gray-500">
            This form doesn't exist or is no longer available.
          </p>
        </div>
      </div>
    );
  }

  // ── Success state ────────────────────────────────────────────────────────────
  if (submitted) {
    const successMsg = form?.settings?.successMessage || form?.successMessage || 'Thank you! Your response has been submitted.';
    const sgt = form?.globalTheme || {};
    const sBgType = sgt?.backgroundType || 'solid';
    // Reuse the same page-background resolution as the live form render
    const buildSuccessPageBg = () => {
      if (sBgType === 'gradient') {
        const dir = sgt?.gradientDirection || 'to bottom';
        const c1 = sgt?.gradientColor1 || '#f0fdf4';
        const c2 = sgt?.gradientColor2 || '#dcfce7';
        return { background: `linear-gradient(${dir}, ${c1}, ${c2})` };
      }
      if (sBgType === 'image' && sgt?.backgroundImage) {
        return {
          backgroundImage: `url(${sgt.backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        };
      }
      if (sBgType === 'solid' && sgt?.backgroundColor) {
        return { backgroundColor: sgt.backgroundColor };
      }
      return {};
    };
    const successPageBgStyle = buildSuccessPageBg();
    const successShowOverlay = sBgType === 'image' && (sgt?.overlayOpacity || 0) > 0;
    const successAccent = sgt?.accentColor || '#0e7572';
    const successLabelColor = sgt?.labelColor || '#111827';
    const successSubColor = sgt?.inputTextColor || sgt?.labelColor || '#6b7280';
    const successFontFamily = sgt?.fontFamily || undefined;

    // Shared success card content (thank-you message + optional reset button)
    const successContent = (
      <div className="text-center max-w-sm relative">
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${successAccent}1a` }}>
          <Icon name="CheckCircle" size={32} color={successAccent} />
        </div>
        <h1 className="text-xl font-semibold mb-2" style={{ color: successLabelColor }}>
          {isPreviewMode ? 'Preview: Submitted!' : 'Submitted!'}
        </h1>
        <p className="text-sm" style={{ color: successSubColor }}>{successMsg}</p>
        {isPreviewMode && (
          <button
            onClick={() => {
              clearDraft();
              visitedPageIdsRef.current = [];
              setSubmitted(false);
              setFormValues({});
              setCurrentStep(0);
              setConvFieldIndex(0);
            }}
            className="mt-4 px-4 py-2 text-sm rounded-lg transition-colors"
            style={{ color: successAccent, borderColor: `${successAccent}4d`, border: '1px solid', backgroundColor: 'transparent' }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${successAccent}1a`; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
          >
            ← Back to form
          </button>
        )}
      </div>
    );

    // ── Split-page success: keep the two-column layout in place ───────────────
    // The split wrapper stays; the success card replaces the form in the form
    // column while the content panel (Image / Video / Text) remains visible.
    const successLayoutMode =
      form?.settings?.layoutMode ||
      (form?.multiStepConfig?.conversationalMode === true ? 'conversational'
        : form?.multiStepConfig?.enabled ? 'multi-step'
        : 'standard');
    if (successLayoutMode === 'split') {
      const spc = form?.settings?.splitPanelConfig || {};
      const panelSide = spc?.panelSide || 'left';
      const ratio = Math.min(80, Math.max(20, spc?.widthRatio || 40));

      const successContentPanel = (
        <div
          style={{
            flex: `0 0 ${ratio}%`,
            minWidth: 260,
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          <SplitPanelContent config={spc} />
        </div>
      );

      const successFormColumn = (
        <div
          style={{ flex: 1, minWidth: 300, padding: '40px 16px', boxSizing: 'border-box', overflowY: 'auto' }}
          className="flex items-center justify-center"
        >
          {successContent}
        </div>
      );

      return (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            minHeight: '100vh',
            backgroundColor: '#f9fafb',
            ...successPageBgStyle,
            position: 'relative',
            fontFamily: successFontFamily,
          }}
        >
          {successShowOverlay && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ backgroundColor: sgt?.overlayColor || '#000000', opacity: (sgt?.overlayOpacity || 0) / 100 }}
            />
          )}
          {panelSide === 'left' ? <>{successContentPanel}{successFormColumn}</> : <>{successFormColumn}{successContentPanel}</>}
        </div>
      );
    }

    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 relative"
        style={{ backgroundColor: '#f9fafb', ...successPageBgStyle, fontFamily: successFontFamily }}
      >
        {successShowOverlay && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ backgroundColor: sgt?.overlayColor || '#000000', opacity: (sgt?.overlayOpacity || 0) / 100 }}
          />
        )}
        {successContent}
      </div>
    );
  }

  if (checkoutState?.state === 'processing') {
    return <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="max-w-sm text-center bg-white rounded-xl p-8 shadow-sm">
        <Icon name="Loader2" className="animate-spin mx-auto mb-4 text-primary" size={30} />
        <h1 className="text-lg font-semibold">Confirming your payment</h1>
        <p className="text-sm text-gray-500 mt-2">{checkoutState.message}</p>
      </div>
    </div>;
  }

  // ── Resolve all form elements ────────────────────────────────────────────────
  // The DB column is `form_elements` → toCamelCase converts it to `formElements`
  const resolveAllFields = () => {
    // Primary: formElements (from form_elements column)
    if (Array.isArray(form?.formElements) && form?.formElements?.length > 0) {
      return form?.formElements;
    }
    // Fallback: pages array (legacy structure)
    if (Array.isArray(form?.pages) && form?.pages?.length > 0) {
      const allFields = form?.pages?.flatMap(p => p?.fields || p?.elements || []);
      if (allFields?.length > 0) return allFields;
    }
    // Fallback: flat fields array
    if (Array.isArray(form?.fields) && form?.fields?.length > 0) return form?.fields;
    if (Array.isArray(form?.formFields) && form?.formFields?.length > 0) return form?.formFields;
    return [];
  };

  const allFields = resolveAllFields();

  // ── Build name→value map for conditional logic evaluation ───────────────────
  const nameValueMap = buildNameValueMap(allFields, formValues);

  // ── Evaluate form-level Conditional Logic panel rules ────────────────────────
  const logicRulesResult = evaluateLogicRules(form?.logicRules, formValues);

  // ── Separate media (logo/image) elements from form fields ────────────────────
  // Media elements (logos, images) are always rendered at the TOP of the form
  // regardless of their position in the formElements array. This ensures the
  // logo always appears as a header even if it was reordered in the builder.
  const mediaElements = allFields?.filter(f => f?.type === 'media');
  const nonMediaFields = allFields?.filter(f => f?.type !== 'media');

  const description = form?.description || '';
  const themeColor = form?.globalTheme?.accentColor || form?.globalTheme?.primaryColor || form?.theme?.primaryColor || form?.primaryColor || '#0e7572';

  // ── Derive page / card background style from globalTheme ─────────────────
  const _gt = form?.globalTheme || {};
  const _bgType = _gt?.backgroundType || 'solid';
  const buildPageBackground = () => {
    if (_bgType === 'gradient') {
      const dir = _gt?.gradientDirection || 'to bottom';
      const c1 = _gt?.gradientColor1 || '#f0fdf4';
      const c2 = _gt?.gradientColor2 || '#dcfce7';
      return { background: `linear-gradient(${dir}, ${c1}, ${c2})` };
    }
    if (_bgType === 'image' && _gt?.backgroundImage) {
      return {
        backgroundImage: `url(${_gt.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    }
    if (_bgType === 'solid' && _gt?.backgroundColor) {
      return { backgroundColor: _gt.backgroundColor };
    }
    return {};
  };
  const pageBackgroundStyle = buildPageBackground();
  const hasPageBackground = Object.keys(pageBackgroundStyle).length > 0;
  // Overlay for image backgrounds
  const showOverlay = _bgType === 'image' && (_gt?.overlayOpacity || 0) > 0;
  const cardStyle = {
    // Card uses a semi-transparent white so the page background peeks through,
    // or a fully white card when no custom background is set.
    backgroundColor: hasPageBackground ? 'rgba(255,255,255,0.92)' : '#ffffff',
    fontFamily: _gt?.fontFamily || undefined,
  };

  // ── Detect multi-step ────────────────────────────────────────────────────────
  // multiStepConfig is stored as JSONB with camelCase keys (fieldIds, progressStyle, etc.)
  // toCamelCase converts the DB column name multi_step_config → multiStepConfig
  // but the nested keys inside the JSONB are already camelCase from the form builder
  const msc = form?.multiStepConfig;
  // layoutMode is stored in settings JSONB (primary path, no new column required).
  // Fall back to the legacy conversationalMode toggle inside multiStepConfig for
  // forms that were saved before this layout selector was introduced.
  const _layoutMode =
    form?.settings?.layoutMode ||
    (msc?.conversationalMode === true ? 'conversational'
      : msc?.enabled ? 'multi-step'
      : 'standard');
  const isMultiStep = _layoutMode === 'multi-step'
    && msc?.enabled === true && Array.isArray(msc?.steps) && msc?.steps?.length > 1;
  const isConversational = _layoutMode === 'conversational';
  const isSplit = _layoutMode === 'split';

  // ── CONVERSATIONAL MODE RENDER ───────────────────────────────────────────────
  if (isConversational) {
    // Build ordered question sequence using the shared helper (same logic; passes
    // logicRulesResult.fieldOverrides so logic-rule visibility/required/disabled
    // overrides are applied on top of per-field dependency rules).
    const questionScreens = buildConvQuestionScreens(
      allFields, nameValueMap, msc, logicRulesResult.fieldOverrides,
    );

    // Guard: file upload in conversational mode cannot be supported because the File object
    // is not JSON-serialisable in the standard submission payload, and no storage pipeline
    // is wired into this render path.
    // Check ALL form fields (not just currently-visible ones) so that conditionally-revealed
    // file fields don't slip through after the renderer has already been selected.
    const hasAnyFileField = allFields.some(f => f?.type === 'file');
    if (!hasAnyFileField) {

    const totalScreens = questionScreens.length;
    const safeIdx = totalScreens > 0 ? Math.min(convFieldIndex, totalScreens - 1) : 0;
    const currentScreen = questionScreens[safeIdx] || { field: null, state: null, prelude: [] };
    const currentField = currentScreen?.field;
    const currentState = currentScreen?.state;
    const isLastScreen = safeIdx === totalScreens - 1;
    const progressPct = totalScreens > 0 ? ((safeIdx + 1) / totalScreens) * 100 : 0;

    const gt = form?.globalTheme || {};
    const convAccent = gt?.accentColor || '#0e7572';
    const convLabelColor = gt?.labelColor || '#111827';
    const convFontFamily = gt?.fontFamily || undefined;
    const convSubmitText = form?.settings?.submitButtonText || form?.submitButtonText || 'Submit';
    const convPaymentEnabled = form?.paymentEnabled && form?.paymentSettings;
    const convFinalButtonText = convPaymentEnabled ? 'Continue to Payment' : convSubmitText;
    const convFinalButtonIcon = convPaymentEnabled ? 'CreditCard' : 'Check';
    const renderConvPaymentAmount = () => {
      if (!convPaymentEnabled || form?.paymentSettings?.mode !== 'variable') return null;
      return (
        <div className="mt-5 space-y-1.5 text-left">
          <label className="block text-sm font-medium" style={{ color: convLabelColor }}>
            Payment Amount ({form.paymentSettings?.currency || 'USD'}) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute inset-y-0 left-4 flex items-center text-sm opacity-60" style={{ color: convLabelColor }}>$</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={paymentAmount}
              onChange={(e) => {
                setPaymentAmount(e.target.value);
                setError(null);
              }}
              placeholder="0.00"
              className="w-full pl-8 pr-4 py-3 text-lg border-2 rounded-lg bg-transparent outline-none"
              style={{ borderColor: convAccent, color: convLabelColor, fontFamily: convFontFamily }}
            />
          </div>
        </div>
      );
    };

    // Helper: start exit animation for the current screen then clear it
    const startExitAnimation = (exitClass) => {
      setConvOutgoing({ screen: currentScreen, exitClass });
      if (convOutgoingTimerRef.current) clearTimeout(convOutgoingTimerRef.current);
      convOutgoingTimerRef.current = setTimeout(() => setConvOutgoing(null), 400);
    };

    // Core advance logic; accepts an optional overrideValue so auto-advance callers can
    // pass the just-selected value before React has committed the formValues state update.
    const handleConvAdvanceWithValue = (overrideFieldId, overrideValue) => {
      if (currentField) {
        const fid = overrideFieldId || currentField?.id || currentField?.name;

        // Block if a cross-field dependency has flagged this field with an error
        if (currentState?.depError) {
          setConvShake(true);
          setTimeout(() => setConvShake(false), 600);
          return;
        }

        // Prefer the explicit override; fall back to committed state (keyboard / OK button).
        const valueToValidate = overrideValue !== undefined ? overrideValue : formValues?.[fid];
        const err = validateField({ ...currentField, required: currentState?.required }, valueToValidate);
        if (err) {
          setValidationErrors(prev => ({ ...prev, [fid]: err }));
          setConvShake(true);
          setTimeout(() => setConvShake(false), 600);
          return;
        }
        setValidationErrors(prev => { const n = { ...prev }; delete n[fid]; return n; });
      }
      if (isLastScreen) { handleSubmit({ preventDefault: () => {} }); return; }
      const convSteps = msc?.steps || [];
      const currentPageIndex = getConversationalScreenPageIndex(currentScreen, convSteps);
      const currentPageId = currentPageIndex >= 0 ? convSteps[currentPageIndex]?.id : null;
      const currentRouteIndex = currentPageId
        ? visitedPageIdsRef.current.lastIndexOf(currentPageId)
        : -1;
      const visitedPageIds = currentRouteIndex >= 0
        ? visitedPageIdsRef.current.slice(0, currentRouteIndex + 1)
        : [...visitedPageIdsRef.current, ...(currentPageId ? [currentPageId] : [])];
      const navigationValues = overrideFieldId
        ? { ...formValues, [overrideFieldId]: overrideValue }
        : formValues;
      const pageScopedValues = buildPageScopedFormValues(
        convSteps,
        visitedPageIds,
        navigationValues,
      );
      const boundaryPageLogic = evaluatePageLogicRules(
        form?.logicRules,
        pageScopedValues,
      );
      const nextScreenIndex = resolveNextConversationalScreenIndex(
        questionScreens,
        safeIdx,
        convSteps,
        boundaryPageLogic,
      );
      if (nextScreenIndex === null) {
        handleSubmit({ preventDefault: () => {} });
        return;
      }
      const nextPageIndex = getConversationalScreenPageIndex(
        questionScreens[nextScreenIndex],
        convSteps,
      );
      const nextPageId = nextPageIndex >= 0 ? convSteps[nextPageIndex]?.id : null;
      if (nextPageId && nextPageId !== visitedPageIds[visitedPageIds.length - 1]) {
        visitedPageIds.push(nextPageId);
      }
      visitedPageIdsRef.current = visitedPageIds;
      startExitAnimation('conv-exit-up');
      setConvDirection('forward');
      setConvFieldIndex(nextScreenIndex);
      setError(null);
    };

    // Public advance — used by the OK button and keyboard Enter
    const handleConvAdvance = () => handleConvAdvanceWithValue(undefined, undefined);

    const handleConvBack = () => {
      // Cancel any pending auto-advance so it can't fire after navigating back
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      if (safeIdx === 0) return;
      const convSteps = msc?.steps || [];
      const currentPageIndex = getConversationalScreenPageIndex(currentScreen, convSteps);
      const previousSequentialPageIndex = getConversationalScreenPageIndex(
        questionScreens[safeIdx - 1],
        convSteps,
      );
      let previousScreenIndex = safeIdx - 1;
      if (
        currentPageIndex >= 0 &&
        previousSequentialPageIndex !== currentPageIndex
      ) {
        const currentPageId = convSteps[currentPageIndex]?.id;
        const currentRouteIndex = visitedPageIdsRef.current.lastIndexOf(currentPageId);
        const previousPageId = currentRouteIndex > 0
          ? visitedPageIdsRef.current[currentRouteIndex - 1]
          : null;
        const previousVisitedScreenIndex = questionScreens.findLastIndex(
          screen =>
            getConversationalScreenPageIndex(screen, convSteps) ===
            convSteps.findIndex(page => page?.id === previousPageId),
        );
        if (previousVisitedScreenIndex >= 0) previousScreenIndex = previousVisitedScreenIndex;
      }
      startExitAnimation('conv-exit-down');
      setConvDirection('backward');
      setConvFieldIndex(previousScreenIndex);
      setError(null);
    };

    // Auto-advance: called immediately after handleChange so we pass the new value
    // explicitly instead of relying on the not-yet-committed formValues state.
    const scheduleAutoAdvance = (fieldId, newValue) => {
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = setTimeout(() => {
        handleConvAdvanceWithValue(fieldId, newValue);
      }, 300);
    };

    // Update stable refs so keyboard listener always calls the current closures
    convAdvanceRef.current = handleConvAdvance;
    convBackRef.current = handleConvBack;
    convContextRef.current = { currentField, currentFormValues: formValues, onHandleChange: handleChange };

    const animClass = convDirection === 'backward' ? 'conv-in-down' : 'conv-in-up';
    const currentFieldId = currentField?.id || currentField?.name;
    const currentError = validationErrors?.[currentFieldId] || currentState?.depError;

    // Auto-advance field types (no OK button; advance immediately on selection).
    // Checkbox is intentionally excluded: respondents need to select multiple options
    // before confirming, so it shows an OK button instead of auto-advancing.
    const AUTO_ADV = new Set(['radio', 'toggle', 'select', 'dropdown']);
    const showOkButton = currentField ? !AUTO_ADV.has(currentField?.type) : true;

    const bigInputStyle = (field) => {
      const fs = field?.fieldStyle || {};
      return {
        width: '100%',
        padding: '12px 16px',
        fontSize: '1.1rem',
        border: `2px solid ${validationErrors?.[field?.id || field?.name] ? '#ef4444' : (fs?.borderColor || convAccent)}`,
        borderRadius: fs?.borderRadius != null ? `${fs.borderRadius}px` : '8px',
        backgroundColor: fs?.inputBg || gt?.inputBackground || 'transparent',
        color: fs?.textColor || gt?.inputTextColor || convLabelColor,
        fontFamily: convFontFamily,
        outline: 'none',
        transition: 'border-color 0.2s',
      };
    };

    const renderConvInput = (field, state) => {
      if (!field) return null;
      const fid = field?.id || field?.name;
      const disabled = state?.disabled;
      const required = state?.required;
      const placeholder = field?.placeholder || '';

      switch (field?.type) {
        case 'textarea':
          return (
            <textarea
              key={fid}
              rows={4}
              placeholder={placeholder}
              required={required}
              disabled={disabled}
              value={formValues?.[fid] || ''}
              onChange={(e) => handleChange(fid, e.target.value)}
              style={{ ...bigInputStyle(field), resize: 'vertical' }}
              autoFocus
            />
          );

        case 'radio':
          return (
            <div className="space-y-2 mt-2">
              {(field?.options || []).map((opt, i) => {
                const val = typeof opt === 'object' ? opt?.value : opt;
                const lbl = typeof opt === 'object' ? opt?.label : opt;
                const isSel = formValues?.[fid] === val;
                return (
                  <button
                    key={i} type="button" disabled={disabled}
                    onClick={() => { handleChange(fid, val); scheduleAutoAdvance(fid, val); }}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all"
                    style={{
                      borderColor: isSel ? convAccent : `${convAccent}40`,
                      backgroundColor: isSel ? `${convAccent}18` : 'transparent',
                      color: convLabelColor, fontFamily: convFontFamily,
                    }}
                  >
                    <span className="w-7 h-7 rounded-full border-2 flex-shrink-0 flex items-center justify-center text-xs font-bold transition-colors"
                      style={{ borderColor: isSel ? convAccent : '#9ca3af', backgroundColor: isSel ? convAccent : 'transparent', color: '#fff' }}>
                      {isSel ? <Icon name="Check" size={13} color="#fff" /> : String.fromCharCode(65 + i)}
                    </span>
                    <span className="text-sm font-medium">{lbl}</span>
                  </button>
                );
              })}
            </div>
          );

        case 'checkbox':
          return (
            <div className="space-y-2 mt-2">
              {(field?.options || []).map((opt, i) => {
                const val = typeof opt === 'object' ? opt?.value : opt;
                const lbl = typeof opt === 'object' ? opt?.label : opt;
                const arr = Array.isArray(formValues?.[fid]) ? formValues[fid] : [];
                const isChk = arr.includes(val);
                return (
                  <button
                    key={i} type="button" disabled={disabled}
                    onClick={() => { const nxt = isChk ? arr.filter(v => v !== val) : [...arr, val]; handleChange(fid, nxt); }}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl border-2 transition-all"
                    style={{
                      borderColor: isChk ? convAccent : `${convAccent}40`,
                      backgroundColor: isChk ? `${convAccent}18` : 'transparent',
                      color: convLabelColor, fontFamily: convFontFamily,
                    }}
                  >
                    <span className="w-5 h-5 rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors"
                      style={{ borderColor: isChk ? convAccent : '#9ca3af', backgroundColor: isChk ? convAccent : 'transparent' }}>
                      {isChk && <Icon name="Check" size={11} color="#fff" />}
                    </span>
                    <span className="text-sm font-medium">{lbl}</span>
                  </button>
                );
              })}
            </div>
          );

        case 'select':
        case 'dropdown':
          return (
            <select
              value={formValues?.[fid] || ''} disabled={disabled} required={required}
              onChange={(e) => { handleChange(fid, e.target.value); if (e.target.value) scheduleAutoAdvance(fid, e.target.value); }}
              style={bigInputStyle(field)}
            >
              <option value="">{placeholder || 'Select an option'}</option>
              {(field?.options || []).map((opt, i) => (
                <option key={i} value={typeof opt === 'object' ? opt?.value : opt}>
                  {typeof opt === 'object' ? opt?.label : opt}
                </option>
              ))}
            </select>
          );

        case 'toggle': {
          const isChk = formValues?.[fid] === 'true' || formValues?.[fid] === true;
          return (
            <button type="button" role="switch" aria-checked={isChk} disabled={disabled}
              onClick={() => { const nv = String(!isChk); handleChange(fid, nv); scheduleAutoAdvance(fid, nv); }}
              className="flex items-center gap-4 mt-3 group"
            >
              <div className="relative w-14 h-7 rounded-full transition-colors" style={{ backgroundColor: isChk ? convAccent : '#d1d5db' }}>
                <span className="absolute top-1.5 w-4 h-4 bg-white rounded-full shadow transition-all" style={{ left: isChk ? 'calc(100% - 20px)' : '6px' }} />
              </div>
              <span className="text-lg font-medium" style={{ color: convLabelColor, fontFamily: convFontFamily }}>
                {isChk ? (field?.onLabel || 'On') : (field?.offLabel || 'Off')}
              </span>
            </button>
          );
        }

        case 'slider': {
          const sMin = field?.sliderMin ?? 0;
          const sMax = field?.sliderMax ?? 100;
          const sStep = field?.sliderStep ?? 1;
          const sDef = field?.sliderDefault ?? Math.round((sMin + sMax) / 2);
          const sVal = formValues?.[fid] != null ? Number(formValues[fid]) : sDef;
          return (
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-4">
                <input type="range" min={sMin} max={sMax} step={sStep} value={sVal} disabled={disabled}
                  onChange={(e) => handleChange(fid, e.target.value)}
                  className="flex-1 h-2 rounded-lg cursor-pointer" style={{ accentColor: convAccent }} />
                <span className="text-2xl font-bold w-16 text-right flex-shrink-0" style={{ color: convAccent, fontFamily: convFontFamily }}>{sVal}</span>
              </div>
              <div className="flex justify-between text-xs" style={{ color: `${convLabelColor}80` }}>
                <span>{sMin}</span><span>{sMax}</span>
              </div>
            </div>
          );
        }

        case 'rating': {
          const rMax = Math.max(1, field?.ratingMax ?? 5);
          const rDef = Math.min(rMax, Math.max(1, field?.ratingDefault ?? Math.ceil(rMax / 2)));
          const rVal = formValues?.[fid] != null ? Number(formValues[fid]) : rDef;
          return (
            <div className="flex items-center gap-1 mt-4 flex-wrap">
              {Array.from({ length: rMax }, (_, i) => {
                const starVal = i + 1;
                const isSel = starVal <= rVal;
                return (
                  <button key={i} type="button" disabled={disabled}
                    onClick={() => { handleChange(fid, starVal); scheduleAutoAdvance(fid, starVal); }}
                    style={{
                      fontSize: '36px', color: isSel ? convAccent : '#d1d5db',
                      background: 'none', border: 'none',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      padding: '0 4px', lineHeight: 1,
                      opacity: disabled ? 0.5 : 1, transition: 'color 0.15s',
                    }}>★</button>
                );
              })}
            </div>
          );
        }

        case 'scale': {
          const scMin = field?.scaleMin ?? 1;
          const scMax = Math.max(scMin + 1, field?.scaleMax ?? 10);
          const scDef = Math.min(scMax, Math.max(scMin, field?.scaleDefault ?? Math.round((scMin + scMax) / 2)));
          const scVal = formValues?.[fid] != null ? Number(formValues[fid]) : scDef;
          return (
            <div className="flex flex-wrap gap-2 mt-4">
              {Array.from({ length: scMax - scMin + 1 }, (_, i) => {
                const v = scMin + i;
                const isSel = v === scVal;
                return (
                  <button key={i} type="button" disabled={disabled}
                    onClick={() => { handleChange(fid, v); scheduleAutoAdvance(fid, v); }}
                    className="w-10 h-10 flex items-center justify-center rounded-xl border-2 text-sm font-bold transition-all"
                    style={{
                      borderColor: isSel ? convAccent : `${convAccent}40`,
                      backgroundColor: isSel ? convAccent : 'transparent',
                      color: isSel ? '#fff' : convLabelColor,
                      fontFamily: convFontFamily,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                    }}>
                    {v}
                  </button>
                );
              })}
            </div>
          );
        }

        case 'number': case 'date': case 'email': case 'phone':
          return (
            <input key={fid} autoFocus
              type={field?.type === 'email' ? 'email' : field?.type === 'phone' ? 'tel' : field?.type === 'date' ? 'date' : 'number'}
              placeholder={field?.type === 'email' ? (placeholder || 'you@example.com') : field?.type === 'phone' ? (placeholder || '+1 (555) 000-0000') : placeholder}
              required={required} disabled={disabled}
              value={formValues?.[fid] || ''}
              onChange={(e) => handleChange(fid, e.target.value)}
              style={bigInputStyle(field)}
            />
          );

        case 'url':
          // Explicit case for elements stored with type: 'url' (defensive, non-standard).
          // Most URL elements from the builder are stored as type: 'input' — handled in default.
          return (
            <input key={fid} type="url" autoFocus
              placeholder={placeholder || 'https://'}
              required={required} disabled={disabled}
              value={formValues?.[fid] || ''}
              onChange={(e) => handleChange(fid, e.target.value)}
              style={bigInputStyle(field)}
            />
          );

        case 'file':
          // File upload is not supported in conversational mode: the browser File object
          // cannot be JSON-serialised into the standard submission payload.
          return (
            <div key={fid} className="mt-2 flex items-start gap-3 p-4 rounded-xl border-2"
              style={{ borderColor: `${convAccent}40`, color: convLabelColor }}>
              <Icon name="Info" size={18} color={convAccent} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium">File upload is not available in conversational mode.</p>
                <p className="text-xs mt-0.5 opacity-60">Use the standard form view to upload files.</p>
              </div>
            </div>
          );

        case 'signature': {
          const fs2 = field?.fieldStyle || {};
          return (
            <ConvSignaturePad
              key={fid}
              value={formValues?.[fid] || ''}
              onChange={(v) => handleChange(fid, v)}
              borderColor={fs2?.borderColor || convAccent}
              strokeColor={fs2?.textColor || gt?.inputTextColor || '#111827'}
            />
          );
        }

        default: {
          // All builder input-category fields (text, email, phone, number, url, date) share
          // type: 'input'. The element ID prefix reveals which kind it actually is.
          const idPrefix = (fid || '').split('_')[0];
          const nativeType =
            idPrefix === 'email'  ? 'email' :
            idPrefix === 'phone'  ? 'tel'   :
            idPrefix === 'number' ? 'number':
            idPrefix === 'url'    ? 'url'   :
            idPrefix === 'date'   ? 'date'  :
            'text';
          const defaultPlaceholder =
            nativeType === 'email'  ? (placeholder || 'you@example.com') :
            nativeType === 'tel'    ? (placeholder || '+1 (555) 000-0000') :
            nativeType === 'url'    ? (placeholder || 'https://') :
            placeholder;
          return (
            <input key={fid} type={nativeType} autoFocus
              placeholder={defaultPlaceholder}
              required={required} disabled={disabled}
              value={formValues?.[fid] || ''}
              onChange={(e) => handleChange(fid, e.target.value)}
              style={bigInputStyle(field)}
            />
          );
        }
      }
    };

    // ── Answer preview formatter for review mode ─────────────────────────────
    const formatAnswerPreview = (field, value) => {
      if (value === undefined || value === null || value === '') return '';
      const type = field?.type || 'input';

      if (type === 'checkbox') {
        if (!Array.isArray(value) || value.length === 0) return '';
        // Resolve labels from options if available
        const opts = field?.options || [];
        const labels = value.map(v => {
          const match = opts.find(o => (typeof o === 'object' ? o?.value : o) === v);
          return match ? (typeof match === 'object' ? match?.label : match) : v;
        });
        return labels.join(', ');
      }

      if (type === 'radio' || type === 'select' || type === 'dropdown') {
        const opts = field?.options || [];
        const match = opts.find(o => (typeof o === 'object' ? o?.value : o) === value);
        return match ? (typeof match === 'object' ? match?.label : match) : String(value);
      }

      if (type === 'toggle') {
        return (value === 'true' || value === true)
          ? (field?.onLabel || 'On')
          : (field?.offLabel || 'Off');
      }

      if (type === 'signature') return 'Signed ✓';

      if (type === 'rating') return `${'★'.repeat(Number(value))}${'☆'.repeat(Math.max(0, (field?.ratingMax ?? 5) - Number(value)))}`;

      if (type === 'scale') return `${value} / ${field?.scaleMax ?? 10}`;

      const str = String(value);
      return str.length > 60 ? str.slice(0, 57) + '…' : str;
    };

    // Count answered questions (for showing/hiding the Review button)
    const answeredCount = questionScreens.filter(s => {
      if (!s.field) return false;
      const fid = s.field?.id || s.field?.name;
      const v = formValues?.[fid];
      return v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
    }).length;

    // Touch-swipe handlers for conversational mode (mobile)
    const handleConvTouchStart = (e) => {
      if (e.touches.length !== 1) return;
      swipeTouchStartXRef.current = e.touches[0].clientX;
    };

    const handleConvTouchEnd = (e) => {
      if (swipeTouchStartXRef.current === null) return;
      // Disable swipe gesture when the active field is a slider (thumb drag conflicts)
      if (currentField?.type === 'slider') {
        swipeTouchStartXRef.current = null;
        return;
      }
      const endX = e.changedTouches[0].clientX;
      const deltaX = endX - swipeTouchStartXRef.current;
      swipeTouchStartXRef.current = null;
      const SWIPE_THRESHOLD = 60;
      if (Math.abs(deltaX) < SWIPE_THRESHOLD) return;
      if (deltaX < 0) {
        // Swiped left → advance
        handleConvAdvance();
      } else {
        // Swiped right → go back
        handleConvBack();
      }
    };

    return (
      <div className="min-h-screen flex flex-col relative overflow-hidden"
        onTouchStart={handleConvTouchStart}
        onTouchEnd={handleConvTouchEnd}
        style={{ backgroundColor: '#f9fafb', ...pageBackgroundStyle, fontFamily: convFontFamily }}>
        {/* Background overlay */}
        {showOverlay && (
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: gt?.overlayColor || '#000000', opacity: (gt?.overlayOpacity || 0) / 100 }} />
        )}

        {/* Fixed top bar: slim progress fill + counter + back */}
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="h-1" style={{ backgroundColor: `${convAccent}25` }}>
            <div className="h-full transition-all duration-500" style={{ width: `${progressPct}%`, backgroundColor: convAccent }} />
          </div>
          <div className="flex items-center justify-between px-4 py-2 bg-white/85 backdrop-blur-sm border-b border-black/5">
            <button type="button" onClick={handleConvBack} disabled={safeIdx === 0}
              className="flex items-center gap-1 text-sm font-medium disabled:opacity-25 transition-opacity"
              style={{ color: convAccent }}>
              <Icon name="ChevronLeft" size={17} />
              Back
            </button>
            <span className="text-xs font-semibold tabular-nums" style={{ color: `${convLabelColor}70` }}>
              {safeIdx + 1} / {totalScreens}
            </span>
            {isPreviewMode
              ? <span className="text-xs text-amber-600 flex items-center gap-1"><Icon name="Eye" size={11} /> Preview</span>
              : answeredCount > 0
                ? (
                  <button type="button" onClick={() => setConvReviewMode(true)}
                    className="flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-80"
                    style={{ color: convAccent }}>
                    <Icon name="ListChecks" size={14} />
                    Review
                  </button>
                )
                : <span className="w-14" />}
          </div>
        </div>

        {/* Question area — vertically centred */}
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-28 relative z-10">
          {/* Outgoing card — animates out while the incoming animates in */}
          {convOutgoing && (
            <div className={`w-full max-w-lg absolute ${convOutgoing.exitClass}`} aria-hidden="true">
              {/* Render a static (non-interactive) snapshot of the outgoing screen */}
              {convOutgoing.screen?.prelude?.length > 0 && (
                <div className="mb-6 space-y-3">
                  {convOutgoing.screen.prelude.map(({ field: lf }, i) => (
                    <div key={i}>{renderField(lf, nameValueMap, logicRulesResult.fieldOverrides)}</div>
                  ))}
                </div>
              )}
              {convOutgoing.screen?.field && (
                <h2 className="text-xl font-semibold leading-snug" style={{ color: convLabelColor }}>
                  {convOutgoing.screen.field?.label || convOutgoing.screen.field?.name}
                </h2>
              )}
            </div>
          )}
          <div key={safeIdx} className={`w-full max-w-lg ${animClass} ${convShake ? 'conv-shake' : ''}`}>
            {renderHoneypot()}

            {/* Prelude layout elements (heading, paragraph, image…) */}
            {(currentScreen?.prelude || []).length > 0 && (
              <div className="mb-6 space-y-3">
                {currentScreen.prelude.map(({ field: lf }, i) => (
                  <div key={i}>{renderField(lf, nameValueMap, logicRulesResult.fieldOverrides)}</div>
                ))}
              </div>
            )}

            {/* Interactive field */}
            {currentField && (
              <div className="space-y-5">
                {/* Question label */}
                <div className="flex items-start gap-2">
                  <span className="text-sm font-bold mt-0.5 flex-shrink-0" style={{ color: `${convAccent}cc` }}>
                    {safeIdx + 1} →
                  </span>
                  <div>
                    <h2 className="text-xl font-semibold leading-snug" style={{ color: convLabelColor }}>
                      {currentField?.label || currentField?.name}
                      {currentState?.required && <span className="text-red-500 ml-1">*</span>}
                    </h2>
                    {currentField?.description && (
                      <p className="text-sm mt-1 opacity-60" style={{ color: convLabelColor }}>{currentField.description}</p>
                    )}
                  </div>
                </div>

                {/* Input */}
                <div>
                  {renderConvInput(currentField, currentState)}

                  {/* Validation / dep error */}
                  {currentError && (
                    <p className="flex items-center gap-1.5 mt-2 text-sm text-red-500">
                      <Icon name="AlertCircle" size={14} />{currentError}
                    </p>
                  )}

                  {/* Variable payment amount is part of the final conversational
                      screen so it is available before creating the PaymentIntent. */}
                  {isLastScreen && renderConvPaymentAmount()}

                  {isLastScreen && renderTurnstile()}

                  {/* Submit-level error */}
                  {error && (
                    <div className="flex items-center gap-2 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                      <Icon name="AlertCircle" size={14} />{error}
                    </div>
                  )}

                  {/* OK / Submit button (text + slider + checkbox types) */}
                  {showOkButton && (
                    <div className="flex items-center gap-3 mt-5">
                      <button type="button" onClick={handleConvAdvance} disabled={submitting || creatingIntent}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold transition-all disabled:opacity-60 shadow-sm"
                        style={{ backgroundColor: convAccent }}>
                        {isLastScreen
                          ? (submitting || creatingIntent)
                            ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />{creatingIntent ? 'Preparing…' : 'Submitting…'}</>
                            : <>{convFinalButtonText} <Icon name={convFinalButtonIcon} size={15} /></>
                          : <>{formValues?.[currentFieldId] !== undefined && formValues?.[currentFieldId] !== '' ? 'OK' : 'Skip'} <Icon name="Check" size={15} /></>}
                      </button>
                      {!isLastScreen && (
                        <span className="text-xs" style={{ color: `${convLabelColor}50` }}>
                          press <kbd className="px-1.5 py-0.5 rounded border text-xs" style={{ borderColor: `${convLabelColor}30` }}>Enter ↵</kbd>
                        </span>
                      )}
                    </div>
                  )}
                  {/* Review answers inline prompt — only on last screen, after OK/Submit button */}
                  {showOkButton && isLastScreen && !isPreviewMode && answeredCount > 0 && (
                    <button type="button" onClick={() => setConvReviewMode(true)}
                      className="mt-3 text-sm flex items-center gap-1.5 opacity-60 hover:opacity-90 transition-opacity"
                      style={{ color: convAccent }}>
                      <Icon name="ListChecks" size={14} />
                      Want to check your answers? Review →
                    </button>
                  )}

                  {/* Auto-advance types: show Submit on last screen; skip link otherwise */}
                  {!showOkButton && isLastScreen && (
                    <div className="mt-6">
                      <button type="button" onClick={handleConvAdvance} disabled={submitting || creatingIntent}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold transition-all disabled:opacity-60 shadow-sm"
                        style={{ backgroundColor: convAccent }}>
                        {(submitting || creatingIntent)
                          ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />{creatingIntent ? 'Preparing…' : 'Submitting…'}</>
                          : <>{convFinalButtonText} <Icon name={convFinalButtonIcon} size={15} /></>}
                      </button>
                    </div>
                  )}
                  {/* Review answers inline prompt — auto-advance last screen */}
                  {!showOkButton && isLastScreen && !isPreviewMode && answeredCount > 0 && (
                    <button type="button" onClick={() => setConvReviewMode(true)}
                      className="mt-3 text-sm flex items-center gap-1.5 opacity-60 hover:opacity-90 transition-opacity"
                      style={{ color: convAccent }}>
                      <Icon name="ListChecks" size={14} />
                      Want to check your answers? Review →
                    </button>
                  )}
                  {!showOkButton && !isLastScreen && !currentState?.required && (
                    <button type="button" onClick={handleConvAdvance}
                      className="mt-4 text-xs flex items-center gap-1 opacity-40 hover:opacity-70 transition-opacity"
                      style={{ color: convLabelColor }}>
                      Skip <Icon name="ChevronRight" size={12} />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Layout-only trailing screen (no interactive field) */}
            {!currentField && (
              <div className="text-center mt-4">
                {isLastScreen && renderConvPaymentAmount()}
                {isLastScreen && renderTurnstile()}
                {error && (
                  <div className="flex items-center gap-2 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-left text-red-600">
                    <Icon name="AlertCircle" size={14} />{error}
                  </div>
                )}
                <button type="button" onClick={handleConvAdvance} disabled={submitting || creatingIntent}
                  className="flex items-center gap-2 mx-auto mt-5 px-6 py-3 rounded-xl text-white text-sm font-semibold transition-all disabled:opacity-60 shadow-sm"
                  style={{ backgroundColor: convAccent }}>
                  {isLastScreen
                    ? (submitting || creatingIntent)
                      ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />{creatingIntent ? 'Preparing…' : 'Submitting…'}</>
                      : <>{convFinalButtonText} <Icon name={convFinalButtonIcon} size={15} /></>
                    : <>Continue <Icon name="ChevronRight" size={15} /></>}
                </button>
                {/* Review answers inline prompt — layout-only last screen */}
                {isLastScreen && !isPreviewMode && answeredCount > 0 && (
                  <button type="button" onClick={() => setConvReviewMode(true)}
                    className="mt-3 text-sm flex items-center gap-1.5 mx-auto opacity-60 hover:opacity-90 transition-opacity"
                    style={{ color: convAccent }}>
                    <Icon name="ListChecks" size={14} />
                    Want to check your answers? Review →
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Powered by */}
        {showPoweredBy && (
          <div className="text-center pb-5 relative z-10">
            <p className="text-xs text-gray-400">
              Powered by{' '}
              <a href="https://webforms.biz" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">WebForms</a>
            </p>
          </div>
        )}

        {/* ── Review-answers overlay ──────────────────────────────────────────── */}
        {convReviewMode && (
          <div
            className="absolute inset-0 z-50 flex flex-col"
            style={{
              backgroundColor: '#f9fafb',
              ...pageBackgroundStyle,
              fontFamily: convFontFamily,
            }}
          >
            {/* Overlay background tint so the page BG stays readable */}
            {showOverlay && (
              <div className="absolute inset-0 pointer-events-none"
                style={{ backgroundColor: gt?.overlayColor || '#000000', opacity: (gt?.overlayOpacity || 0) / 100 }} />
            )}

            {/* Header */}
            <div className="relative z-10 flex items-center justify-between px-4 py-3 bg-white/90 backdrop-blur-sm border-b border-black/8">
              <div className="flex items-center gap-2">
                <Icon name="ListChecks" size={18} color={convAccent} />
                <h2 className="text-base font-semibold" style={{ color: convLabelColor }}>Review your answers</h2>
              </div>
              <button
                type="button"
                onClick={() => setConvReviewMode(false)}
                className="p-1.5 rounded-lg transition-colors hover:bg-black/8"
                style={{ color: convLabelColor }}
                aria-label="Close review"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            {/* Scrollable question list */}
            <div className="relative z-10 flex-1 overflow-y-auto py-4 px-4 space-y-2">
              {questionScreens.map((screen, idx) => {
                if (!screen.field) return null;
                const fid = screen.field?.id || screen.field?.name;
                const val = formValues?.[fid];
                const hasAnswer = val !== undefined && val !== '' && !(Array.isArray(val) && val.length === 0);
                const answerPreview = hasAnswer ? formatAnswerPreview(screen.field, val) : '';
                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => {
                      setConvFieldIndex(idx);
                      setConvDirection('forward');
                      setConvReviewMode(false);
                    }}
                    className="w-full text-left rounded-xl border-2 px-4 py-3 transition-all hover:shadow-sm"
                    style={{
                      borderColor: hasAnswer ? `${convAccent}50` : `${convAccent}1a`,
                      backgroundColor: hasAnswer ? `${convAccent}08` : 'rgba(255,255,255,0.6)',
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium" style={{ color: convLabelColor }}>
                          <span className="font-normal opacity-40 mr-1">{idx + 1}.</span>
                          {screen.field.label || screen.field.name}
                          {screen.state?.required && <span className="text-red-500 ml-0.5">*</span>}
                        </p>
                        {hasAnswer && answerPreview && (
                          <p className="text-sm mt-0.5 truncate font-medium" style={{ color: convAccent }}>
                            {answerPreview}
                          </p>
                        )}
                        {!hasAnswer && (
                          <p className="text-xs mt-0.5 opacity-35 italic" style={{ color: convLabelColor }}>
                            Not answered
                          </p>
                        )}
                      </div>
                      <Icon name="Pencil" size={13} color={convAccent} className="flex-shrink-0 mt-1 opacity-50" />
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Footer: close or go to submit */}
            <div className="relative z-10 px-4 py-4 bg-white/85 backdrop-blur-sm border-t border-black/5 flex gap-3">
              <button
                type="button"
                onClick={() => setConvReviewMode(false)}
                className="flex-1 py-2.5 rounded-xl border-2 text-sm font-semibold transition-colors"
                style={{ borderColor: `${convAccent}50`, color: convAccent, backgroundColor: 'transparent' }}
              >
                Continue
              </button>
              <button
                type="button"
                onClick={() => {
                  setConvFieldIndex(totalScreens - 1);
                  setConvDirection('forward');
                  setConvReviewMode(false);
                }}
                className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white transition-colors shadow-sm"
                style={{ backgroundColor: convAccent }}
              >
                Go to Submit
              </button>
            </div>
          </div>
        )}
      </div>
    );
    } // end if (!hasRequiredFileField) — falls through to standard multi-step renderer otherwise
  }

  // ── MULTI-STEP RENDER ────────────────────────────────────────────────────────
  if (isMultiStep) {
    const steps = msc?.steps || [];
    const totalSteps = steps?.length;
    const safeStep = Math.min(currentStep, totalSteps - 1);
    const step = steps?.[safeStep];
    const isLastStep = safeStep === totalSteps - 1;
    const progressStyle = msc?.progressStyle || 'bar';
    const showStepCount = msc?.showStepCount !== false;
    const showStepTitles = msc?.showStepTitles !== false;
    const nextText = msc?.nextButtonText || 'Next';
    const backText = msc?.backButtonText || 'Back';
    const submitText = form?.settings?.submitButtonText || form?.submitButtonText || 'Submit';

    // Get fields for the current step by matching fieldIds
    // fieldIds are stored as string values (not keys) so they survive toCamelCase unchanged
    const stepFieldIds = step?.fieldIds || [];
    // For multi-step, only include non-media fields in steps; media is shown above
    const stepFields = stepFieldIds
      ?.map(id => nonMediaFields?.find(f => (f?.id === id)))
      ?.filter(Boolean);

    // If a step has no assigned fields, fall back to showing all fields not assigned to any step
    const assignedIds = new Set(steps?.flatMap(s => s?.fieldIds || []));
    const unassignedFields = nonMediaFields?.filter(f => !assignedIds?.has(f?.id));

    const fieldsToRender = stepFields?.length > 0 ? stepFields : unassignedFields;

    const handleNext = (e) => {
      e?.preventDefault();
      // Block step advancement if any visible field on this step has a setError dependency
      const stepDepError = fieldsToRender
        .map(f => computeEffectiveFieldState(f, nameValueMap, logicRulesResult.fieldOverrides))
        .find(s => s.visible && s.depError);
      if (stepDepError) {
        setError(stepDepError.depError);
        return;
      }
      // Run custom validation rules for this step's visible fields
      const stepValErrors = {};
      let hasStepError = false;
      for (const f of fieldsToRender) {
        const state = computeEffectiveFieldState(f, nameValueMap, logicRulesResult.fieldOverrides);
        if (!state.visible) continue;
        const id = f?.id || f?.name;
        const err = validateField({ ...f, required: state.required }, formValues?.[id]);
        if (err) { stepValErrors[id] = err; hasStepError = true; }
      }
      if (hasStepError) {
        setValidationErrors(prev => ({ ...prev, ...stepValErrors }));
        setError('Please fix the highlighted errors before continuing.');
        return;
      }
      if (safeStep < totalSteps - 1) {
        const currentPageId = steps[safeStep]?.id;
        const currentRouteIndex = currentPageId
          ? visitedPageIdsRef.current.lastIndexOf(currentPageId)
          : -1;
        const visitedPageIds = currentRouteIndex >= 0
          ? visitedPageIdsRef.current.slice(0, currentRouteIndex + 1)
          : [...visitedPageIdsRef.current, ...(currentPageId ? [currentPageId] : [])];
        const pageScopedValues = buildPageScopedFormValues(
          steps,
          visitedPageIds,
          formValues,
        );
        const boundaryPageLogic = evaluatePageLogicRules(
          form?.logicRules,
          pageScopedValues,
        );
        const nextStep = resolveNextPageIndex(steps, safeStep, boundaryPageLogic);
        const nextPageId = steps[nextStep]?.id;
        if (nextPageId && nextPageId !== visitedPageIds[visitedPageIds.length - 1]) {
          visitedPageIds.push(nextPageId);
        }
        visitedPageIdsRef.current = visitedPageIds;
        setCurrentStep(nextStep);
        setError(null);
        window?.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    const handleBack = () => {
      if (safeStep > 0) {
        const currentPageId = steps[safeStep]?.id;
        const currentRouteIndex = visitedPageIdsRef.current.lastIndexOf(currentPageId);
        const previousPageId = currentRouteIndex > 0
          ? visitedPageIdsRef.current[currentRouteIndex - 1]
          : null;
        const previousStep = previousPageId
          ? steps.findIndex(page => page?.id === previousPageId)
          : safeStep - 1;
        setCurrentStep(previousStep >= 0 ? previousStep : safeStep - 1);
        setError(null);
        window?.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    // Progress indicator
    const renderProgress = () => {
      if (progressStyle === 'none' || progressStyle === 'footer-bar') return null;

      if (progressStyle === 'bar') {
        const pct = ((safeStep + 1) / totalSteps) * 100;
        return (
          <div className="mb-6">
            {showStepCount && (
              <div className="flex justify-between text-xs text-gray-500 mb-1.5">
                <span>Step {safeStep + 1} of {totalSteps}</span>
                <span>{Math.round(pct)}%</span>
              </div>
            )}
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${pct}%`, background: themeColor }}
              />
            </div>
          </div>
        );
      }

      if (progressStyle === 'steps') {
        return (
          <div className="flex items-center justify-center gap-2 mb-6">
            {steps?.map((s, idx) => (
              <React.Fragment key={s?.id || idx}>
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-all duration-200"
                  style={
                    idx < safeStep
                      ? { background: themeColor, borderColor: themeColor, color: '#fff' }
                      : idx === safeStep
                      ? { background: 'transparent', borderColor: themeColor, color: themeColor }
                      : { background: 'transparent', borderColor: '#d1d5db', color: '#9ca3af' }
                  }
                >
                  {idx < safeStep ? <Icon name="Check" size={14} /> : idx + 1}
                </div>
                {idx < totalSteps - 1 && (
                  <div
                    className="flex-1 h-0.5 max-w-[40px] transition-all duration-200"
                    style={{ background: idx < safeStep ? themeColor : '#e5e7eb' }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        );
      }

      if (progressStyle === 'numbered') {
        return (
          <div className="flex items-center gap-1.5 mb-6 flex-wrap">
            {steps?.map((s, idx) => (
              <div
                key={s?.id || idx}
                className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-all duration-200"
                style={
                  idx === safeStep
                    ? { background: themeColor, borderColor: themeColor, color: '#fff' }
                    : idx < safeStep
                    ? { background: `${themeColor}20`, borderColor: themeColor, color: themeColor }
                    : { background: 'transparent', borderColor: '#e5e7eb', color: '#9ca3af' }
                }
              >
                <span>{idx + 1}</span>
                {showStepTitles && s?.title && <span>{s?.title}</span>}
              </div>
            ))}
          </div>
        );
      }

      return null;
    };

    return (
      <div className="min-h-screen py-10 px-4 relative" style={{ backgroundColor: '#f9fafb', ...pageBackgroundStyle }}>
        {/* Image background overlay */}
        {showOverlay && (
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: _gt?.overlayColor || '#000000', opacity: (_gt?.overlayOpacity || 0) / 100 }} />
        )}
        {isPreviewMode && (
          <div className="max-w-xl mx-auto mb-3 relative z-10">
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <Icon name="Eye" size={13} />
              <span>Preview mode — submissions won't be recorded</span>
            </div>
          </div>
        )}
        <div style={{ maxWidth: form?.globalTheme?.formMaxWidth ? `${form.globalTheme.formMaxWidth}px` : '576px' }} className="mx-auto relative z-10">
          <div
            className="rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            style={{
              ...cardStyle,
              ...(progressStyle === 'footer-bar' ? {
                borderBottomLeftRadius: `${msc?.footerBorderRadius ?? 0}px`,
                borderBottomRightRadius: `${msc?.footerBorderRadius ?? 0}px`,
              } : undefined),
            }}
          >
            {/* Media / logo elements — always rendered at the TOP */}
            {mediaElements?.length > 0 && (
              <div className="px-6 pt-6 md:px-8 md:pt-8 space-y-4">
                {mediaElements?.map((field) => renderField(field, nameValueMap, logicRulesResult.fieldOverrides))}
              </div>
            )}
            <div className="p-6 md:p-8">
              {/* Progress indicator */}
              {renderProgress()}

              {/* Step title */}
              {showStepTitles && step?.title && progressStyle !== 'numbered' && (
                <h2 className="text-base font-semibold text-gray-800 mb-4">{step?.title}</h2>
              )}

              {/* Step fields */}
              <form
                ref={footerFormRef}
                onSubmit={isLastStep ? handleSubmit : handleNext}
                className="space-y-5"
              >
                {renderHoneypot()}
                {fieldsToRender?.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">This step has no fields.</p>
                ) : (
                  fieldsToRender?.map((field) => renderField(field, nameValueMap, logicRulesResult.fieldOverrides))
                )}

                {/* Calculated field results — shown on last step */}
                {isLastStep && renderCalcFields(nameValueMap)}

                {/* Variable-amount payment input — shown for ALL progress styles on last step */}
                {isLastStep && form?.paymentEnabled && form?.paymentSettings?.mode === 'variable' && (
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Payment Amount ({form.paymentSettings?.currency || 'USD'}) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 text-sm">$</span>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        placeholder="0.00"
                        className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                )}

                {isLastStep && renderTurnstile()}

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    <Icon name="AlertCircle" size={15} />
                    {error}
                  </div>
                )}

                {/* Navigation buttons — hidden when footer-bar handles navigation */}
                {progressStyle !== 'footer-bar' && (
                  <div className="space-y-3 pt-2">
                    <div className={`flex gap-3 ${safeStep > 0 ? 'justify-between' : 'justify-end'}`}>
                      {safeStep > 0 && (
                        <button
                          type="button"
                          onClick={handleBack}
                          className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50 transition-all"
                        >
                          <Icon name="ChevronLeft" size={16} />
                          {backText}
                        </button>
                      )}

                      <button
                        type="submit"
                        disabled={submitting || creatingIntent}
                        className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-60"
                        style={{ background: themeColor }}
                      >
                        {isLastStep ? (
                          (submitting || creatingIntent) ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                              {creatingIntent ? 'Preparing Payment…' : 'Submitting…'}
                            </>
                          ) : form?.paymentEnabled ? (
                            <>
                              <Icon name="CreditCard" size={16} />
                              Continue to Payment
                            </>
                          ) : (
                            <>
                              {submitText}
                              <Icon name="Check" size={16} />
                            </>
                          )
                        ) : (
                          <>
                            {nextText}
                            <Icon name="ChevronRight" size={16} />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Footer Bar — rendered flush at card bottom, outside the padded content area */}
            {progressStyle === 'footer-bar' && (
              <div
                className="flex items-center justify-between px-6 py-4 relative"
                style={{ background: form?.globalTheme?.footerColor || darkenHex(themeColor) }}
              >
                {safeStep > 0 ? (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex items-center gap-1.5 text-sm font-semibold text-white/90 hover:text-white transition-colors"
                  >
                    <Icon name="ChevronLeft" size={16} />
                    {backText}
                  </button>
                ) : <span />}

                {showStepCount && (
                  <span className="absolute left-1/2 -translate-x-1/2 text-xs text-white/60 pointer-events-none">
                    Step {safeStep + 1} of {totalSteps}
                  </span>
                )}

                <button
                  type="button"
                  disabled={submitting || creatingIntent}
                  onClick={() => {
                    if (footerFormRef.current && !footerFormRef.current.reportValidity()) return;
                    // Block if any visible step field has an active setError dependency
                    const footerDepError = fieldsToRender
                      .map(f => computeEffectiveFieldState(f, nameValueMap, logicRulesResult.fieldOverrides))
                      .find(s => s.visible && s.depError);
                    if (footerDepError) { setError(footerDepError.depError); return; }
                    // Run custom validation rules for visible step fields
                    const footerValErrors = {};
                    let hasFooterError = false;
                    for (const f of fieldsToRender) {
                      const state = computeEffectiveFieldState(f, nameValueMap, logicRulesResult.fieldOverrides);
                      if (!state.visible) continue;
                      const id = f?.id || f?.name;
                      const err = validateField({ ...f, required: state.required }, formValues?.[id]);
                      if (err) { footerValErrors[id] = err; hasFooterError = true; }
                    }
                    if (hasFooterError) {
                      setValidationErrors(prev => ({ ...prev, ...footerValErrors }));
                      setError('Please fix the highlighted errors before continuing.');
                      return;
                    }
                    if (isLastStep) handleSubmit(); else handleNext();
                  }}
                  className="flex items-center gap-1.5 text-sm font-semibold text-white hover:text-white/80 transition-colors disabled:opacity-60"
                >
                  {isLastStep ? (
                    (submitting || creatingIntent) ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        {creatingIntent ? 'Preparing…' : 'Submitting…'}
                      </>
                    ) : form?.paymentEnabled ? (
                      <>
                        <Icon name="CreditCard" size={16} />
                        Pay
                      </>
                    ) : (
                      <>
                        {submitText}
                        <Icon name="Check" size={16} />
                      </>
                    )
                  ) : (
                    <>
                      {nextText}
                      <Icon name="ChevronRight" size={16} />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Powered by */}
          {showPoweredBy && (
            <p className="text-center text-xs text-gray-400 mt-4">
              Powered by{' '}
              <a href="https://webforms.biz" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
                WebForms
              </a>
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── SPLIT-PAGE RENDER ────────────────────────────────────────────────────────
  if (isSplit) {
    const spc = form?.settings?.splitPanelConfig || {};
    const panelSide = spc?.panelSide || 'left';
    const ratio = Math.min(80, Math.max(20, spc?.widthRatio || 40));

    const contentPanel = (
      <div
        style={{
          flex: `0 0 ${ratio}%`,
          minWidth: 260,
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        <SplitPanelContent config={spc} />
      </div>
    );

    const formColumn = (
      <div style={{ flex: 1, minWidth: 300, padding: '40px 16px', boxSizing: 'border-box', overflowY: 'auto' }}>
        {isPreviewMode && (
          <div className="max-w-xl mx-auto mb-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
              <Icon name="Eye" size={13} />
              <span>Preview mode — submissions won't be recorded</span>
            </div>
          </div>
        )}
        <div style={{ maxWidth: form?.globalTheme?.formMaxWidth ? `${form.globalTheme.formMaxWidth}px` : '576px' }} className="mx-auto">
          <div className="rounded-2xl shadow-sm border border-gray-100 overflow-hidden" style={cardStyle}>
            {mediaElements?.length > 0 && (
              <div className="px-6 pt-6 md:px-8 md:pt-8 space-y-4">
                {mediaElements?.map((field) => renderField(field, nameValueMap, logicRulesResult.fieldOverrides))}
              </div>
            )}
            <div className="p-6 md:p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                {renderHoneypot()}
                {nonMediaFields?.length === 0 && mediaElements?.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-6">This form has no fields.</p>
                ) : nonMediaFields?.length === 0 ? null : (
                  nonMediaFields?.map((field) => renderField(field, nameValueMap, logicRulesResult.fieldOverrides))
                )}
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    <Icon name="AlertCircle" size={15} />
                    {error}
                  </div>
                )}
                {nonMediaFields?.length > 0 && (
                  <>
                    {renderCalcFields(nameValueMap)}
                    {form?.paymentEnabled && form?.paymentSettings?.mode === 'variable' && (
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          Payment Amount ({form.paymentSettings?.currency || 'USD'}) <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 text-sm">$</span>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={paymentAmount}
                            onChange={(e) => setPaymentAmount(e.target.value)}
                            placeholder="0.00"
                            className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                        </div>
                      </div>
                    )}
                    {renderTurnstile()}
                    <button
                      type="submit"
                      disabled={submitting || creatingIntent}
                      className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-60"
                      style={{ background: themeColor }}
                    >
                      {(submitting || creatingIntent) ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          {creatingIntent ? 'Preparing Payment…' : 'Submitting…'}
                        </>
                      ) : form?.paymentEnabled ? (
                        <>
                          <Icon name="CreditCard" size={16} />
                          Continue to Payment
                        </>
                      ) : (
                        form?.settings?.submitButtonText || form?.submitButtonText || 'Submit'
                      )}
                    </button>
                  </>
                )}
              </form>
            </div>
          </div>
          {showPoweredBy && (
            <p className="text-center text-xs text-gray-400 mt-4">
              Powered by{' '}
              <a href="https://webforms.biz" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
                WebForms
              </a>
            </p>
          )}
        </div>
      </div>
    );

    return (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          minHeight: '100vh',
          backgroundColor: '#f9fafb',
          ...pageBackgroundStyle,
          position: 'relative',
        }}
      >
        {showOverlay && (
          <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: _gt?.overlayColor || '#000000', opacity: (_gt?.overlayOpacity || 0) / 100 }} />
        )}
        {panelSide === 'left' ? <>{contentPanel}{formColumn}</> : <>{formColumn}{contentPanel}</>}
      </div>
    );
  }

  // ── SINGLE-STEP RENDER ───────────────────────────────────────────────────────
  return (
    <div className="min-h-screen py-10 px-4 relative" style={{ backgroundColor: '#f9fafb', ...pageBackgroundStyle }}>
      {/* Image background overlay */}
      {showOverlay && (
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: _gt?.overlayColor || '#000000', opacity: (_gt?.overlayOpacity || 0) / 100 }} />
      )}
      {isPreviewMode && (
        <div className="max-w-xl mx-auto mb-3 relative z-10">
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
            <Icon name="Eye" size={13} />
            <span>Preview mode — submissions won't be recorded</span>
          </div>
        </div>
      )}
      <div style={{ maxWidth: form?.globalTheme?.formMaxWidth ? `${form.globalTheme.formMaxWidth}px` : '576px' }} className="mx-auto relative z-10">
        {/* Form card */}
        <div className="rounded-2xl shadow-sm border border-gray-100 overflow-hidden" style={cardStyle}>
          {/* Media / logo elements — always rendered at the TOP, outside the form tag */}
          {mediaElements?.length > 0 && (
            <div className="px-6 pt-6 md:px-8 md:pt-8 space-y-4">
              {mediaElements?.map((field) => renderField(field, nameValueMap, logicRulesResult.fieldOverrides))}
            </div>
          )}
          <div className="p-6 md:p-8">
            {/* Form — no hardcoded title; title/logo are form elements in formElements array */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {renderHoneypot()}
              {nonMediaFields?.length === 0 && mediaElements?.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">This form has no fields.</p>
              ) : nonMediaFields?.length === 0 ? null : (
                nonMediaFields?.map((field) => renderField(field, nameValueMap, logicRulesResult.fieldOverrides))
              )}

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  <Icon name="AlertCircle" size={15} />
                  {error}
                </div>
              )}

              {nonMediaFields?.length > 0 && (
                <>
                  {/* Calculated field results */}
                  {renderCalcFields(nameValueMap)}

                  {/* Variable-amount payment input */}
                  {form?.paymentEnabled && form?.paymentSettings?.mode === 'variable' && (
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Payment Amount ({form.paymentSettings?.currency || 'USD'}) <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <span className="absolute inset-y-0 left-3 flex items-center text-gray-400 text-sm">$</span>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          placeholder="0.00"
                          className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                    </div>
                  )}
                  {renderTurnstile()}
                  <button
                    type="submit"
                    disabled={submitting || creatingIntent}
                    className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-60"
                    style={{ background: themeColor }}
                  >
                    {(submitting || creatingIntent) ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        {creatingIntent ? 'Preparing Payment…' : 'Submitting…'}
                      </>
                    ) : form?.paymentEnabled ? (
                      <>
                        <Icon name="CreditCard" size={16} />
                        Continue to Payment
                      </>
                    ) : (
                      form?.settings?.submitButtonText || form?.submitButtonText || 'Submit'
                    )}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>

        {/* Powered by */}
        {showPoweredBy && (
          <p className="text-center text-xs text-gray-400 mt-4">
            Powered by{' '}
            <a href="https://webforms.biz" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 transition-colors">
              WebForms
            </a>
          </p>
        )}
      </div>
    </div>
  );
};

export default PublicFormViewer;
